<template>
  <div class="patient-profile-page">
    <!-- Modern Header matching Admin/Wiki/Forms style -->
    <div class="profile-header">
      <div class="header-left">
        <h1 class="page-title">Patient Profile</h1>
      </div>
      <div class="header-actions">
        <!-- Placeholder for any future header actions -->
      </div>
    </div>

    <ErrorDisplay/>

    <PatientProfileSkeleton v-if="!patientData" />

    <b-row v-show="patientData">
      <b-col xl="6" xs="12">
        <!-- PATIENT INFO PANEL START -->
        <div class="patient-info-card">
          <!-- Patient Header -->
          <div class="patient-header">
            <div class="patient-avatar">
              <PatientPhotoAvatar
                :site-id="siteID"
                :pat-num="patientNum"
                :enabled="patientProfilePhotoEnabled"
              />
            </div>
            <div class="patient-info">
              <div v-if="patientData" class="patient-name">
                {{formatName(patientData.PatientName)}}
                <span class="patient-details">{{formatAge(getAge(patientData.Birthdate))}}{{getGender(patientData.Gender)}}</span>
                <b-badge variant="danger" v-if="patientData.Premed === 1" class="premed-badge">! Premed</b-badge>
                <b-badge variant="info" v-if="patientData.Language === 'spa'" class="language-badge" title="Patient prefers Spanish">ES</b-badge>
              </div>
              <!-- Insurance Verification status chip (6b). Rendered only when the
                   soft-gated patient-status endpoint returns enabled:true, so it
                   adds zero layout footprint on sites without the feature. -->
              <div v-if="insuranceStatus" class="patient-insurance-chip">
                <InsuranceStatusChip
                  :status="insuranceStatus.status"
                  :last-verified="insuranceStatus.lastVerified"
                  :last-checked="insuranceStatus.lastChecked"
                  :is-stale="insuranceStatus.isStale"
                  :never-verified="insuranceStatus.neverVerified"
                  :timezone="allSettings && allSettings.GENERAL_TIMEZONE ? allSettings.GENERAL_TIMEZONE.PropertyValue : ''"
                  @navigate="goToInsuranceVerification"
                />
              </div>
            </div>
          </div>
          <!-- Quick Stats Bar - HIDDEN: Not working correctly, needs fixing -->
          <!-- TODO: Fix Last Visit, Next Appt, Recall Due data sources before re-enabling -->
          <div class="patient-quick-stats" v-if="false && patientData">
            <div class="stat-item">
              <i class="fa fa-calendar-check-o"></i>
              <div class="stat-content">
                <span class="stat-label">Last Visit</span>
                <span class="stat-value">{{ patientData.DateLastVisit ? formatDateShort(patientData.DateLastVisit) : 'N/A' }}</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa fa-clock-o"></i>
              <div class="stat-content">
                <span class="stat-label">Next Appt</span>
                <span class="stat-value">{{ nextAppointmentDate || 'Not Scheduled' }}</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa fa-refresh"></i>
              <div class="stat-content">
                <span class="stat-label">Recall Due</span>
                <span class="stat-value" :class="{'text-danger': isRecallOverdue}">{{ patientData.DateDue ? formatDateShort(patientData.DateDue) : 'N/A' }}</span>
              </div>
            </div>
            <div class="stat-item" v-if="patientData.EstBalance !== undefined">
              <i class="fa fa-dollar"></i>
              <div class="stat-content">
                <span class="stat-label">Balance</span>
                <span class="stat-value" :class="{'text-danger': patientData.EstBalance > 0}">{{ formatCurrency(patientData.EstBalance) }}</span>
              </div>
            </div>
          </div>
          <!-- Action Buttons -->
          <div class="action-buttons profile-actions-modern">
            <b-dropdown id="dropdown-form" boundary="window" variant="success" size="sm" class="action-dropdown profile-action-primary" ref="initialFollowupDropdown">
                      <template v-slot:button-content>
                        <i class="fa fa-bell-o mr-1" /><span class="fw-semi-bold">Initiate a Follow-up</span>
                      </template>
                      <!--
                        only display the following error message if both recall and unscheduled tx follow-ups can't be created
                       --> 
                     
                      <b-card v-if="unscheduledTxFollowUpCreationAllowed === false && overdueRecallFollowUpCreationAllowed === false"
                        class="border-0 mb-1 mb-1 createFuError"
                      >
                        <p class="text-danger fw-semi-bold mb-1 mt-1">
                          <i class="fa fa-warning text-secondary"></i>                          
                          Cannot Create Follow-up</p>
                          <p @click="showHelpPopOver = !showHelpPopOver"><span class="fw-semi-bold text-danger"><a style="text-decoration: underline;">Here's Why...</a></span></p>
                        <hr/>
                      </b-card>
                      
                      <b-dropdown-form class="profile-addfollowup">
                          <b-form-group
                            class="radio abc-radio mb-0"
                            label=" Select type of Follow-up:"
                            label-align-sm="left"
                          >
                          <b-form-radio-group class="pt-2" id="followupType" v-model="followupType" name="radio-group-1-name">
                              <b-form-radio value="4" :disabled="!unscheduledTxFollowUpCreationAllowed">Unscheduled Tx</b-form-radio>
                              <b-form-radio value="3" :disabled="!overdueRecallFollowUpCreationAllowed">Recall</b-form-radio>   
                            </b-form-radio-group>
                          </b-form-group>                          
                          <hr/>
                          <b-form-group label=" Start Following up On:">
                              <DatePicker                                                
                                  lang="en" 
                                  :format="dateFormatStr"
                                  placeholder="Select a Date" 
                                  input-class="form-control"
                                  v-model="initialFollowupDate"
                                  :disabled="unscheduledTxFollowUpCreationAllowed === false && overdueRecallFollowUpCreationAllowed === false"           
                                  append-to-body>
                                  <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                              </DatePicker>
                          </b-form-group>
                          <b-form-group @submit.stop.prevent>
                            <textarea v-model="initialFollowupNote" rows="3" max-rows="3" cols="10" name :placeholder="followupNotePlaceholder" class="form-control form-control-sm"></textarea>
                          </b-form-group>  
                          <!--
                          <b-button variant="primary" size="sm">Save</b-button>
                          -->
                          <b-row align-h="between" align-v="end">
                            <b-col cols="auto" class="mr-auto">
                                <b-button variant="primary" size="sm" :disabled="saveDisabled" @click="saveInitialFollowupDate">Save</b-button>
                            </b-col>            
                            <b-col cols="auto">
                              <span id="help_addToFollowUp"><i class="fa fa-question-circle text-warning" style="font-size: 18px; cursor: pointer;"></i></span>
                              <b-popover target="help_addToFollowUp" triggers="hover focus" placement="bottom" custom-class="help-info-popover">
                                <div class="popover-help-content">
                                  <!-- Unscheduled Tx Section -->
                                  <div class="help-section">
                                    <div class="help-section-header">
                                      <i class="fa fa-calendar-plus-o"></i>
                                      <span>Unscheduled Tx Options</span>
                                    </div>
                                    <p class="help-section-subtitle">Option may be disabled if:</p>
                                    <ul class="help-reason-list">
                                      <li><i class="fa fa-check-circle"></i> Active follow-up already exists</li>
                                      <li><i class="fa fa-ban"></i> <b-badge variant="danger" size="sm" pill>Do Not Follow-up</b-badge> status is set</li>
                                      <li><i class="fa fa-flag-checkered"></i> Max follow-ups reached <b-badge variant="warning" size="sm" pill>Follow-ups done</b-badge></li>
                                      <li><i class="fa fa-file-o"></i> No unscheduled treatment in plan</li>
                                      <li><i class="fa fa-calendar-check-o"></i> Active appointment already scheduled</li>
                                    </ul>
                                  </div>

                                  <div class="help-divider"></div>

                                  <!-- Recall Section -->
                                  <div class="help-section">
                                    <div class="help-section-header">
                                      <i class="fa fa-calendar"></i>
                                      <span>Recall Options</span>
                                    </div>
                                    <p class="help-section-subtitle">Option may be disabled if:</p>
                                    <ul class="help-reason-list">
                                      <li><i class="fa fa-check-circle"></i> Active follow-up already exists for Recall</li>
                                      <li><i class="fa fa-ban"></i> <b-badge variant="danger" size="sm" pill>Do Not Follow-up</b-badge> status is set</li>
                                      <li><i class="fa fa-flag-checkered"></i> Max follow-ups reached <b-badge variant="warning" size="sm" pill>Follow-ups done</b-badge></li>
                                      <li><i class="fa fa-calendar-check-o"></i> Recall appointment already scheduled</li>
                                    </ul>
                                  </div>
                                </div>
                              </b-popover>
                            </b-col>
                        </b-row>
  
                      </b-dropdown-form>
                  </b-dropdown> 

                  <!-- ============================================= -->
                  <!-- SEND-TO-PATIENT GROUP (Form / Text / Email) -->
                  <!-- ============================================= -->
                  <div class="profile-send-group">
                    <span class="profile-send-group-label">Send to patient</span>

                  <!-- ============================================= -->
                  <!-- SEND FORM BUTTON -->
                  <!-- ============================================= -->
                  <span class="disabled-button-wrapper" :id="!canSendForms ? 'form-btn-wrapper' : null">
                    <b-button
                      variant="outline-secondary"
                      class="btn profile-send-btn"
                      @click="sendForm"
                      :disabled="!canSendForms"
                    >
                      <i class="fa fa-file-text-o mr-1" /> Form
                      <i v-if="!canSendForms" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!canSendForms"
                    target="form-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div v-if="!formsPaidFor" class="feature-upsell-card" data-test="send-form-upsell">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-file-text-o"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Enable Digital Forms</h6>
                          <p class="upsell-desc">Send intake forms, consent forms, and health history forms directly to patients.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="FORMS"
                            feature-label="Digital Forms"
                            source-page="Patient Profile - Send Form"
                          />
                          <FeatureRequestNote feature="FORMS" />
                        </div>
                      </div>
                      <div v-else class="missing-info-card">
                        <div class="info-header">
                          <i class="fa fa-info-circle"></i>
                          <span>Forms Unavailable</span>
                        </div>
                        <ul class="reason-list">
                          <li v-for="(reason, index) in formsDisabledReasons" :key="index">
                            <i :class="`fa ${reason.icon}`"></i>
                            {{ reason.text }}
                          </li>
                          <li v-if="formsDisabledReasons.length === 0">
                            <i class="fa fa-info-circle"></i>
                            You may need to create Forms before sending to patients
                          </li>
                        </ul>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- SEND TEXT BUTTON -->
                  <!-- ============================================= -->
                  <span class="disabled-button-wrapper" :id="!canSendSMS ? 'sms-btn-wrapper' : null">
                    <b-button
                      variant="outline-secondary"
                      class="btn profile-send-btn"
                      @click="sendText"
                      :disabled="!canSendSMS"
                    >
                      <i class="fa fa-comment-o mr-1" /> Text
                      <i v-if="!canSendSMS" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!canSendSMS"
                    target="sms-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div v-if="!smsFeatureEnabled" class="feature-upsell-card">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-mobile"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Enable 2-Way SMS</h6>
                          <p class="upsell-desc">Send appointment reminders, confirmations, and have real-time conversations with patients.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="SMS"
                            feature-label="2-Way SMS texting"
                            source-page="Patient Profile - SMS"
                          />
                          <FeatureRequestNote feature="SMS" />
                        </div>
                      </div>
                      <div v-else class="missing-info-card">
                        <div class="info-header">
                          <i class="fa fa-info-circle"></i>
                          <span>SMS Unavailable</span>
                        </div>
                        <ul class="reason-list">
                          <li v-for="(reason, index) in smsDisabledReasons" :key="index">
                            <i :class="`fa ${reason.icon}`"></i>
                            {{ reason.text }}
                          </li>
                        </ul>
                        <div v-if="smsDisabledTip" class="info-tip">
                          <i class="fa fa-lightbulb-o"></i>
                          <span>{{ smsDisabledTip }}</span>
                        </div>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- SEND EMAIL BUTTON -->
                  <!-- ============================================= -->
                  <span class="disabled-button-wrapper" :id="!canSendEmail ? 'email-btn-wrapper' : null">
                    <b-button
                      variant="outline-secondary"
                      class="btn profile-send-btn"
                      @click="sendEmail"
                      :disabled="!canSendEmail"
                    >
                      <i class="fa fa-envelope-o mr-1" /> Email
                      <i v-if="!canSendEmail" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!canSendEmail"
                    target="email-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div v-if="!emailFeatureEnabled" class="feature-upsell-card">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-envelope-o"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Enable Email Feature</h6>
                          <p class="upsell-desc">Send appointment reminders, confirmations, and follow-ups directly to patients via email.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="EMAIL"
                            feature-label="Patient email"
                            source-page="Patient Profile - Email"
                          />
                          <FeatureRequestNote feature="EMAIL" />
                        </div>
                      </div>
                      <div v-else class="missing-info-card">
                        <div class="info-header">
                          <i class="fa fa-info-circle"></i>
                          <span>Email Unavailable</span>
                        </div>
                        <ul class="reason-list">
                          <li v-for="(reason, index) in emailDisabledReasons" :key="index">
                            <i :class="`fa ${reason.icon}`"></i>
                            {{ reason.text }}
                          </li>
                        </ul>
                        <div class="info-tip">
                          <i class="fa fa-lightbulb-o"></i>
                          <span>Update patient info in Open Dental to enable email.</span>
                        </div>
                      </div>
                    </div>
                  </b-popover>
                  </div><!-- /profile-send-group -->

                  <!-- ============================================= -->
                  <!-- GET FORM QR BUTTON (in-office handoff) -->
                  <!-- ============================================= -->
                  <span class="disabled-button-wrapper" :id="!formsPaidFor ? 'form-qr-btn-wrapper' : null">
                    <b-button
                      variant="outline-primary"
                      class="btn profile-tool-btn"
                      @click="getFormQR"
                      :disabled="!formsPaidFor"
                      title="Show a QR code for a form to hand off to the patient in-office"
                    >
                      <i class="fa fa-qrcode mr-1" /> Get Form QR
                      <i v-if="!formsPaidFor" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!formsPaidFor"
                    target="form-qr-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div class="feature-upsell-card">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-qrcode"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Enable Digital Forms</h6>
                          <p class="upsell-desc">Generate QR codes for in-office form handoff &mdash; no patient search needed.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="FORMS"
                            feature-label="Digital Forms"
                            source-page="Patient Profile - Get Form QR"
                          />
                          <FeatureRequestNote feature="FORMS" />
                        </div>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- TX PLANS BUTTON (Slice 11 Phase C) -->
                  <!-- Per-site gated via TX_PRESENTER_ENABLED. Super Admin -->
                  <!-- always has access regardless of the flag. -->
                  <!-- ============================================= -->
                  <b-button
                    v-if="canAccessTxPresenter"
                    variant="outline-primary"
                    class="btn profile-tool-btn"
                    @click="onOpenTxPlans"
                    :disabled="txPlansDispatcherLoading"
                    title="Send a treatment plan to this patient"
                  >
                    <i class="fa fa-file-text-o mr-1"></i>
                    <span v-if="txPlansDispatcherLoading">Loading…</span>
                    <span v-else>Tx Plans</span>
                  </b-button>
                  <!-- Not paid for (TX_PRESENTER_ENABLED off, not Super Admin): promote it. -->
                  <span v-if="!canAccessTxPresenter" class="disabled-button-wrapper" id="tx-plans-btn-wrapper" data-test="tx-plans-upsell">
                    <b-button variant="outline-primary" class="btn profile-tool-btn" disabled>
                      <i class="fa fa-file-text-o mr-1"></i> Tx Plans
                      <i class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!canAccessTxPresenter"
                    target="tx-plans-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div class="feature-upsell-card">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-file-text-o"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Enable Treatment Presenter</h6>
                          <p class="upsell-desc">Send treatment plans to patients and collect their signatures online.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="TX_PRESENTER"
                            feature-label="Treatment Presenter"
                            source-page="Patient Profile - Tx Plans"
                          />
                          <FeatureRequestNote feature="TX_PRESENTER" />
                        </div>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- REVIEW REQUEST BUTTON (Reviews A7) -->
                  <!-- Per-site gated via $can('view','reviews'). Disabled -->
                  <!-- with an explanatory popover when the patient has -->
                  <!-- neither a mobile number NOR an email on file (there's -->
                  <!-- no channel to send on). Mirrors the disabled-button- -->
                  <!-- wrapper + popover convention used above. -->
                  <!-- ============================================= -->
                  <span
                    v-if="$can('view','reviews')"
                    class="disabled-button-wrapper"
                    :id="!reviewRequestPrereqMet ? 'review-request-btn-wrapper' : null"
                  >
                    <b-button
                      variant="outline-primary"
                      class="btn profile-tool-btn"
                      @click="onOpenReviewRequest"
                      :disabled="!reviewRequestPrereqMet"
                      title="Send a review request to this patient"
                    >
                      <i class="fa fa-star mr-1"></i>
                      Review Request
                      <i v-if="!reviewRequestPrereqMet" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="$can('view','reviews') && !reviewRequestPrereqMet"
                    target="review-request-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div class="missing-info-card">
                        <div class="info-header">
                          <i class="fa fa-info-circle"></i>
                          <span>Review Request Unavailable</span>
                        </div>
                        <ul class="reason-list">
                          <li>
                            <i class="fa fa-address-book"></i>
                            No mobile number or email on file for this patient.
                          </li>
                        </ul>
                        <div class="info-tip">
                          <i class="fa fa-lightbulb-o"></i>
                          <span>Add a phone or email in Open Dental to send review requests.</span>
                        </div>
                      </div>
                    </div>
                  </b-popover>
                  <!-- Not paid for (REVIEWS_PAID_FOR off): promote it. A paid site whose role
                       can't use Reviews sees nothing, as before. -->
                  <span v-if="showReviewsUpsell" class="disabled-button-wrapper" id="review-request-upsell-wrapper" data-test="reviews-upsell">
                    <b-button variant="outline-primary" class="btn profile-tool-btn" disabled>
                      <i class="fa fa-star mr-1"></i> Review Request
                      <i class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="showReviewsUpsell"
                    target="review-request-upsell-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div class="feature-upsell-card">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-star"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Enable Review Requests</h6>
                          <p class="upsell-desc">Ask patients for a Google review by text or email.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="REVIEWS"
                            feature-label="Review Requests"
                            source-page="Patient Profile - Review Request"
                          />
                          <FeatureRequestNote feature="REVIEWS" />
                        </div>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- COLLECT PAYMENT BUTTON -->
                  <!-- Always rendered. Disabled with an explanatory popover -->
                  <!-- when payment isn't enabled OR no provider is properly -->
                  <!-- configured. Mirrors the Form/Text/Email -->
                  <!-- disabled-button-wrapper + popover pattern above so -->
                  <!-- operators get the same UX everywhere. -->
                  <!-- ============================================= -->
                  <span class="disabled-button-wrapper" :id="!canCollectPayment ? 'collect-payment-btn-wrapper' : null">
                    <b-button
                      variant="outline-success"
                      class="btn profile-tool-btn"
                      @click="onOpenCollectPayment"
                      :disabled="!canCollectPayment || collectPaymentOpening"
                      title="Send a payment link or charge a terminal"
                    >
                      <i v-if="collectPaymentOpening" class="fa fa-spinner fa-spin mr-1"></i>
                      <i v-else class="fa fa-dollar mr-1"></i>
                      Collect Payment
                      <i v-if="!canCollectPayment" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!canCollectPayment"
                    target="collect-payment-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content">
                      <div class="missing-info-card">
                        <div class="info-header">
                          <i class="fa fa-info-circle"></i>
                          <span>Collect Payment Unavailable</span>
                        </div>
                        <ul class="reason-list">
                          <li v-for="(reason, index) in collectPaymentDisabledReasons" :key="index">
                            <i :class="`fa ${reason.icon}`"></i>
                            {{ reason.text }}
                          </li>
                        </ul>
                        <div class="info-tip">
                          <i class="fa fa-lightbulb-o"></i>
                          <span>Open <strong>Settings → Payment</strong> to fix.</span>
                        </div>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- CREATE TASK BUTTON (2026-09-02) -->
                  <!-- Deliberately NOT role-gated ("anyone should be able to -->
                  <!-- create a task" — Saumil). Opens the shared TaskComposer -->
                  <!-- in the right slide-out, pre-tagged with this patient. -->
                  <!-- ============================================= -->
                  <b-button
                    variant="outline-primary"
                    class="btn profile-tool-btn"
                    data-test="create-task-btn"
                    @click="onCreateTask"
                    title="Create a task for this patient"
                  >
                    <i class="fa fa-check-square-o mr-1"></i>
                    Create Task
                  </b-button>

                  <!-- ============================================= -->
                  <!-- PHOTO TO OD BUTTON (2026-09-16, Harrisburg Smiles) -->
                  <!-- Take/upload a document photo (insurance card, med list) -->
                  <!-- and file it into the OD image category staff pick. Same -->
                  <!-- gate as the profile photo (All-in-One); off while the demo -->
                  <!-- mask is on. The flow itself is PatientOdImageUpload below. -->
                  <!-- ============================================= -->
                  <span class="disabled-button-wrapper" :id="!patientProfilePhotoEnabled ? 'photo-to-od-btn-wrapper' : null">
                    <b-button
                      variant="outline-primary"
                      class="btn profile-tool-btn"
                      data-test="photo-to-od-btn"
                      @click="onOpenPhotoToOd"
                      :disabled="!patientProfilePhotoEnabled || isDemoModeOn"
                      :title="isDemoModeOn ? 'Turned off while the demo mask is on' : 'Take or upload a photo into Open Dental Imaging'"
                    >
                      <i class="fa fa-camera mr-1"></i>
                      Photo to OD
                      <i v-if="!patientProfilePhotoEnabled" class="fa fa-question-circle text-warning ml-1" style="font-size: 12px;"></i>
                    </b-button>
                  </span>
                  <b-popover
                    v-if="!patientProfilePhotoEnabled"
                    target="photo-to-od-btn-wrapper"
                    triggers="hover focus"
                    placement="bottom"
                    custom-class="disabled-feature-popover"
                  >
                    <div class="popover-feature-content" data-test="photo-to-od-upgrade-popover">
                      <div class="feature-upsell-card">
                        <div class="upsell-icon-wrap">
                          <i class="fa fa-camera"></i>
                        </div>
                        <div class="upsell-content">
                          <h6 class="upsell-title">Upgrade to send photos to Open Dental</h6>
                          <p class="upsell-desc">Snap insurance cards, medication lists and other documents on your phone and file them straight into Open Dental Imaging. Included in the All-in-One package. Upgrade your subscription to turn it on.</p>
                          <FeatureRequestButton
                            class="upsell-cta-btn"
                            feature-key="ALL_IN_ONE"
                            feature-label="All-in-One package (Photo to OD)"
                            source-page="Patient Profile - Photo to OD"
                          />
                          <FeatureRequestNote feature="ALL_IN_ONE" />
                        </div>
                      </div>
                    </div>
                  </b-popover>

                  <!-- ============================================= -->
                  <!-- AI CALL BUTTON -->
                  <!-- ============================================= -->
                  <button
                    class="btn btn-sm ai-call-btn profile-ai-call-btn"
                    :class="{ 'ai-call-loading': aiCallInProgress }"
                    @click="onAICall"
                    :disabled="!canAICall || aiCallInProgress"
                    v-if="canAICall"
                    title="Trigger AI Call (simulation)"
                  >
                    <i v-if="aiCallInProgress" class="fa fa-spinner fa-spin"></i>
                    <span v-else>&#x1F916;</span>
                  </button>
          </div>

          <!-- Photo to OD — the sheet flow behind the "Photo to OD" button. Kept OUT of
               the action row: its root is an empty element, and a flex item there
               would add a gap. -->
          <PatientOdImageUpload
            ref="photoToOd"
            :site-id="siteID"
            :pat-num="patientNum"
            :enabled="patientProfilePhotoEnabled"
          />

          <!-- Get Form QR Modal -->
          <GetFormQRModal
            v-model="showGetFormQRModal"
            :patient-data="patientData"
            :patient-num="patientNum"
            :site-id="siteID"
            :timezone="allSettings.GENERAL_TIMEZONE && allSettings.GENERAL_TIMEZONE.PropertyValue"
          />

          <!-- Slice 11 Phase C — Tx Plans dispatcher + Send modal.
               Both render via store-driven v-if so the dispatcher hands off
               cleanly to SendPlanModal once the TC picks a plan. -->
          <PatientTxPlansModal v-if="txPlansDispatcherOpen" />
          <SendPlanModal v-if="sendPlanOpen" :patient="sendPlanPatientForModal" :timezone="smsOptOutTimezone" />

          <!-- Collect Payment modal — chooser (SMS / Email / Both / Terminal)
               with custom-amount entry. Self-contained: owns its own state,
               wiki loading, and terminal state machine. -->
          <CollectPaymentModal
            v-if="showCollectPaymentModal && patientData"
            v-model="showCollectPaymentModal"
            :site-id="siteID"
            :pat-num="patientNum"
            :patient-data="patientData"
            :patient-balance="resolvedPatientBalance"
            :user="user"
            :site="getSite(siteID)"
            :text-to-pay-settings="collectPaymentTextToPaySettings || {}"
            :signed-plans="signedPlans"
            :timezone="smsOptOutTimezone"
            :prefill="collectPaymentPrefill"
          />

          <!-- Review request modal (Reviews A7) — per-patient opt-out + manual
               "Send review request now". Self-contained: owns its own status
               load, opt-out toggle, and send flow. -->
          <ReviewRequestModal
            v-if="showReviewRequestModal && patientData"
            v-model="showReviewRequestModal"
            :site-id="siteID"
            :pat-num="patientNum"
            :patient-data="patientData"
          />

          <!-- new widget end -->
          <!-- CONTACT INFO PANEL START -->
          <div class="task-details-item contact-info-section" v-if="patientData">
            <FormUpdateBadge :site-id="siteID" :pat-num="patientNum" />
            <div class="task-details-item-header">
              <h5 class="section-title">
                <i class="fa fa-address-book section-icon"></i>
                Contact Information
              </h5>
            </div>
            <div class="section-content">
              <div class="contact-grid" v-if="patientData.HomePhone !== '' || patientData.WirelessPhone !== '' || patientData.WorkPhone !== '' || patientData.Email !== ''">
                <div v-if="patientData.WirelessPhone !== ''" class="contact-card">
                  <div class="contact-card-icon mobile">
                    <i class="fa fa-mobile"></i>
                  </div>
                  <div class="contact-card-content">
                    <span class="contact-card-label">Mobile</span>
                    <a :href="'tel:' + patientData.WirelessPhone" class="contact-card-value">{{patientData.WirelessPhone}}</a>
                  </div>
                </div>
                <div v-if="patientData.HomePhone !== ''" class="contact-card">
                  <div class="contact-card-icon home">
                    <i class="fa fa-home"></i>
                  </div>
                  <div class="contact-card-content">
                    <span class="contact-card-label">Home</span>
                    <a :href="'tel:' + patientData.HomePhone" class="contact-card-value">{{patientData.HomePhone}}</a>
                  </div>
                </div>
                <div v-if="patientData.WorkPhone !== ''" class="contact-card">
                  <div class="contact-card-icon work">
                    <i class="fa fa-building-o"></i>
                  </div>
                  <div class="contact-card-content">
                    <span class="contact-card-label">Work</span>
                    <a :href="'tel:' + patientData.WorkPhone" class="contact-card-value">{{patientData.WorkPhone}}</a>
                  </div>
                </div>
                <div v-if="patientData.Email !== ''" class="contact-card">
                  <div class="contact-card-icon email">
                    <i class="fa fa-envelope"></i>
                  </div>
                  <div class="contact-card-content">
                    <span class="contact-card-label">Email</span>
                    <a :href="'mailto:' + patientData.Email" class="contact-card-value">{{patientData.Email}}</a>
                  </div>
                </div>
              </div>
              <!-- Empty State for Contact Information -->
              <div v-else class="empty-state-box">
                <div class="empty-state-icon">
                  <i class="fa fa-address-book"></i>
                </div>
                <h6 class="empty-state-title">No Contact Information</h6>
                <p class="empty-state-text">No contact details available. Update patient info in Open Dental.</p>
              </div>
            </div>
          </div>
        <!-- NAME PANEL END -->

          <!-- SCHEDULED PANEL START -->
          <div class="task-details-item">
            <div class="task-details-item-header">
              <h5 class="section-title">
                <i class="fa fa-calendar-check-o section-icon"></i>
                Scheduled Appointments
              </h5>
            </div>
            <div class="section-content">
              <div class="table-resposive" v-if="scheduledAppointments && scheduledAppointments.length > 0">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Procedure </th>
                      <th>Provider</th>
                      <th>Date & Time</th>
                      <th>Appointment Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in scheduledAppointments" :key="index">
                      <td>{{appt.ProcDescript}}</td>
                      <td>{{appt.Provider}}</td>
                      <td>{{appt.AptDateTime | dateformatwithtime(allSettings.GENERAL_TIMEZONE.PropertyValue)}}</td>
                      <td>{{appt.AppointmentNotes}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Empty State for Scheduled Appointments -->
              <div v-else class="empty-state-box">
                <div class="empty-state-icon">
                  <i class="fa fa-calendar-check-o"></i>
                </div>
                <h6 class="empty-state-title">No Scheduled Appointments</h6>
                <p class="empty-state-text">This patient doesn't have any upcoming scheduled appointments.</p>
              </div>
            </div>
          </div>
          <!-- SCHEDULED PANEL END -->

          <!-- UNSCHEDULED PANEL START -->
          <div class="task-details-item">
            <div class="task-details-item-header">
              <h5 class="section-title">
                <i class="fa fa-stethoscope section-icon"></i>
                Unscheduled Treatment in Treatment Plan
              </h5>
            </div>
            <div class="section-content">
              <v-client-table v-if="unScheduledTreatments && unScheduledTreatments.length > 0" :data="unScheduledTreatments" :columns="unscheduledTreatmentColumns" :options="options">
                <div slot="h__Priority">Priority</div>
                <div slot="h__ProcCode">Procedure</div>
                <div slot="h__ToothNum">Tooth/Surf</div>
                <div slot="h__Descript">Description</div>
                <div slot="h__Provider">Provider</div>
                <div slot="h__DateTP">Tx Plan Date</div>

                <div slot="ToothNum" slot-scope="props">
                    <span v-if="getCountry === 'CANADA'">{{convertUniversalToFDI(props.row.ToothNum)}}</span>
                    <span v-else>{{props.row.ToothNum}}
                    <span v-if="props.row.Surf !== ''">-</span>{{props.row.Surf}}</span>
                </div>

                <div slot="DateTP" slot-scope="props">
                  {{props.row.DateTP | dateformat(dateFormatStr)}}
                </div>
              </v-client-table>
              <!-- Empty State for Unscheduled Treatment -->
              <div v-else class="empty-state-box">
                <div class="empty-state-icon">
                  <i class="fa fa-stethoscope"></i>
                </div>
                <h6 class="empty-state-title">No Unscheduled Treatments</h6>
                <p class="empty-state-text">This patient doesn't have any unscheduled treatments in their treatment plan.</p>
              </div>
            </div>
          </div>
       <!-- UNSCHEDULD PANEL END -->

        <!-- RECALL PANEL START (condensed) -->
        <div class="task-details-item">
          <div class="task-details-item-header">
            <h5 class="section-title">
              <i class="fa fa-calendar section-icon"></i>
              Recall Appointment
            </h5>
            <div class="status-badges" style="margin-left: auto;">
              <span v-if="patientData && patientData.RecallScheduled !== null">
                <b-badge variant="success" class="text-white" pill>Scheduled</b-badge>
                <span class="ml-2"><i class="fa fa-calendar fa-fw mr-1"/>{{patientData.RecallScheduled | dateformat(dateFormatStr)}}</span>
              </span>
              <span v-else class="text-muted"><small>No recall scheduled</small></span>
            </div>
          </div>
        </div>
        <!-- RECALL PANEL END -->

        <!-- ASAP PANEL START -->
        <div class="task-details-item">
          <div class="task-details-item-header">
            <h5 class="section-title">
              <i class="fa fa-clock-o section-icon"></i>
              ASAP Appointments
            </h5>
            <div v-if="!asapApptList || asapApptList.length === 0" class="status-badges" style="margin-left: auto;">
              <span class="text-muted"><small>None</small></span>
            </div>
          </div>

          <div class="section-content" v-if="asapApptList && asapApptList.length > 0">
            <b-button
              variant="dark"
              class="btn create-new-list ml-2 mb-2"
              @click="updateAsap"
            >
              <i class="fa text-success mb-xs" /> Update Availability
            </b-button>

            <v-client-table :data="asapApptList" :columns="asapApptListColumns" :options="options">
              <div slot="h__AptStatus">Apt Status</div>
              <div slot="h__AppointmentDate">Apt Date</div>
              <div slot="h__ProcDescript">Procedures</div>
            </v-client-table>
          </div>
        </div>
        <!-- ASAP PANEL END -->

          <!-- PLANNED APPOINTMENTS START -->
          <div class="task-details-item">
            <div class="task-details-item-header">
              <h5 class="section-title">
                <i class="fa fa-calendar-plus-o section-icon"></i>
                Planned Appointments
              </h5>
            </div>
            <div class="section-content">
              <div class="table-resposive" v-if="plannedAppointments && plannedAppointments.length > 0">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Procedure(s)</th>
                      <th>Note</th>
                      <th>Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in plannedAppointments" :key="index">
                      <td>{{appt.ProcDescript}}</td>
                      <td>{{appt.Note}}</td>
                      <td>{{appt.Provider}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Empty State for Planned Appointments -->
              <div v-else class="empty-state-box">
                <div class="empty-state-icon">
                  <i class="fa fa-calendar-plus-o"></i>
                </div>
                <h6 class="empty-state-title">No Planned Appointments</h6>
                <p class="empty-state-text">This patient doesn't have any planned appointments in their treatment plan.</p>
              </div>
            </div>
          </div>
       <!-- PLANNED APPOINTMENTS END -->

       <!-- BROKEN PANEL START -->
       <div class="task-details-item">
         <div class="task-details-item-header">
           <h5 class="section-title">
             <i class="fa fa-exclamation-triangle section-icon text-danger"></i>
             Broken Appointment(s) History
           </h5>
         </div>
                  <div class="section-content">
           <div role="alert" aria-live="polite" aria-atomic="true" class="alert alert-dismissible alert-success" v-if="brokenAppointments && brokenAppointments.length === 0">
            <span class="fw-semi-bold">
              <span class="glyphicon glyphicon-thumbs-up"></span> No history of broken appointment(s)
            </span>
            </div>
            <div v-if="brokenAppointments && brokenAppointments.length > 0">
              <h5 class="fw-semi-bold mt-3"><span class="text-danger">{{brokenAppointments.length}}</span> Broken Appointments</h5>
              <div class="table-resposive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Procedure code / Adjustment</th>
                      <th>Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in brokenAppointments" :key="index">
                      <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                      <td><span class="badge text-black badge-gray badge-pill">{{appt.AdjustProcCode}}</span></td>
                      <td>{{appt.Provider}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      <!-- BROKEN PANEL END -->
    </div>
    <!-- FIRST PANEL END -->
    <!-- POST NOTE PANEL START 
    <div class="user-info-panel">
      <section class="note-section">
        <h4 class="fw-semi-bold">Post a note</h4>
        <b-form class="form-section">
          <b-form-group>
            <p class="image"><img class="img-fluid" src="../../assets/icons-user-40.png" alt="..." /></p>
            <b-form-textarea :rows="3" id="default-textarea"/>
          </b-form-group> 
          <b-form-group class="text-right my-4">
            <b-button variant="primary" type="submit" class="mr-xs btn-success">Save to Communication Log</b-button>
            <b-button variant="primary" type="submit" class="mr-xs btn-success">View Communication Log</b-button>
          </b-form-group> 
        </b-form>
        <p>Clicking Save to Comm Log will save the note to the patient's chart as a Communication Log entry.
        </p>
      </section>
    </div>  
     POST NOTE END -->
  </b-col>
  <b-col xl="6" xs="12">
    
    <!-- Follow-ups redesign (docs/plans/2026-10-02-followups-redesign-plan.md, Phase 5). Super
         Admins only until RELEASED.patientProfile is flipped (src/utils/followups/uiVersion.js);
         everyone else gets the v-else section below, unchanged. Same rows (upcomingFollowups),
         patched in place by the shared follow-up actions; the drawer opens through this page's
         one <slideout-panel>. -->
    <div v-if="fuProfileNewUi && patientData && upcomingFollowups && upcomingFollowups.length > 0" class="fu-pp" data-test="fu-patient-profile">
      <followups-profile-section
        :rows="upcomingFollowups"
        :profile-patient-id="patientID"
        :patient-name="patientData.PatientName || ''"
        :profile-facts="fuProfileFacts"
      >
        <template #actions>
          <followups-ui-toggle target="old" note-text="Super Admin preview — offices still see the classic section." @toggle="fuSetUi" />
        </template>
      </followups-profile-section>
    </div>
    <template v-else>
    <followups-ui-toggle v-if="isSuperAdmin && patientData && upcomingFollowups && upcomingFollowups.length > 0" target="new" note-text="Super Admin: a redesigned Follow-ups section is ready to check." @toggle="fuSetUi" />
    <div class="task-details-item follow-up-section" v-if="patientData && upcomingFollowups && upcomingFollowups.length > 0">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-tasks section-icon"></i>
          Follow-ups
        </h5>
      </div>
      <div class="section-content">
        <div class="table-responsive followup-table-wrap">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Attempts</th>
                <th></th>
              </tr>
            </thead>
            <tbody>              
              <tr v-for="(ufp,index) in this.upcomingFollowups" :key="index">
                <!-- TYPE -->
                <td>
                  <span class="fw-semi-bold">{{ ufp.TaskTypeDisplayName }}</span>
                </td>
                
                <!-- STATUS -->
                <td>
                  <b-button v-if="ufp.Status === 'Appointment Scheduled'" variant="success" disabled class="btn-xs">
                    <small><strong>{{ ufp.Status }}</strong></small>
                  </b-button>
                  <b-button v-else variant="default" disabled class="btn-xs">
                    <small><strong>{{ ufp.Status || 'Not Called' }}</strong></small>
                  </b-button>
                </td>
                
                <!-- DUE DATE -->
                <td>
                  <i class="fa fa-spinner fa-spin" style="font-size:16px;" v-if="followupActionPlanLoading[index]"/>
                  
                  <!-- Due Today - Editable -->
                  <b-dropdown v-else-if="isDueToday(ufp.FollowupDate) && $can('update', 'followupDate')" boundary="window" variant="white" size="sm" :ref="`${index}-change-followup-date-dropdown`">
                    <template v-slot:button-content>
                      <b-badge variant="danger" pill>Due Today</b-badge>
                    </template>
                    <b-dropdown-form class="followup-date-form">
                      <b-form-group label="Change Follow-up Date to:"> 
                        <DatePicker v-model="newFollowupDates[index]" lang="en" :format="dateFormatStr" placeholder="Select a Date" input-class="form-control" :not-before="notBefore" append-to-body>
                          <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                        </DatePicker>
                      </b-form-group>                      
                      <b-button :disabled="followupDateChangeInProgress[index]" variant="primary" size="sm" @click="changeFollowupDate($event, index, newFollowupDates[index], ufp)">Save</b-button>
                      <hr/>
                      <b-button variant="danger" size="sm" v-if="(ufp.Status === 'Not Called' || ufp.Status === null) && ufp.CreatedAutomatically === 1 && ufp.InitialFollowupDateSet === 1" @click="showSureModal(ufp, 'sure-modal-remove-initial-followup-date', index)">Remove Initial Follow-up</b-button>
                      <b-button variant="danger" size="sm" @click="showSureModal(ufp, 'sure-modal-stop-followups', index)" v-else>Stop Follow-ups</b-button>
                    </b-dropdown-form>
                  </b-dropdown>
                  
                  <!-- Due Today - Not Editable -->
                  <b-badge v-else-if="isDueToday(ufp.FollowupDate)" variant="danger" pill>Due Today</b-badge>
                  
                  <!-- Stopped (Do Not Follow-up) -->
                  <b-badge v-else-if="ufp.TeamioFollowupStatusID === 3 || ufp.FollowupStopped === 1" variant="danger" pill>Do Not Follow-up</b-badge>
                  
                  <!-- Complete (Follow-ups done) -->
                  <b-badge v-else-if="ufp.FollowupDate === null && ufp.Status !== 'Not Called' && ufp.FollowupStopped === 0" variant="warning" pill>Follow-ups done</b-badge>
                  
                  <!-- Normal Date - Not Editable -->
                  <span v-else-if="(ufp.Status != 'Appointment Scheduled') && !$can('update', 'followupDate')" class="followup-date-display">
                    <i class="fa fa-calendar text-muted mr-1"></i>
                    <span class="fw-semi-bold">{{ufp.FollowupDate | dateformat(dateFormatStr)}}</span>
                  </span>
                  
                  <!-- Normal Date - Editable -->
                  <b-dropdown v-else-if="ufp.Status != 'Appointment Scheduled' && $can('update', 'followupDate')" boundary="window" variant="default" size="sm" :ref="`${index}-change-followup-date-dropdown`">
                    <template v-slot:button-content>
                      <i class="fa fa-calendar text-muted mr-1"></i>
                      <span class="fw-semi-bold">{{ufp.FollowupDate | dateformat(dateFormatStr)}}</span>
                    </template>
                    <b-dropdown-form class="followup-date-form">
                      <b-form-group label="Change Follow-up Date to:"> 
                        <DatePicker v-model="newFollowupDates[index]" lang="en" :format="dateFormatStr" placeholder="Select a Date" input-class="form-control" :not-before="notBefore" append-to-body>
                          <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                        </DatePicker>
                      </b-form-group>                      
                      <b-button :disabled="followupDateChangeInProgress[index]" variant="primary" size="sm" @click="changeFollowupDate($event, index, newFollowupDates[index], ufp)">Save</b-button>
                      <hr/>
                      <b-button variant="danger" size="sm" v-if="(ufp.Status === 'Not Called' || ufp.Status === null) && ufp.CreatedAutomatically === 1 && ufp.InitialFollowupDateSet === 1" @click="showSureModal(ufp, 'sure-modal-remove-initial-followup-date', index)">Remove Initial Follow-up</b-button>
                      <b-button variant="danger" size="sm" @click="showSureModal(ufp, 'sure-modal-stop-followups', index)" v-else>Stop Follow-ups</b-button>
                    </b-dropdown-form>
                  </b-dropdown>
                  
                  <!-- Indicator Icons -->
                  <span class="followup-indicators ml-2">
                    <i v-if="canResetFollowup(ufp)" :id="`followup-reset-${ufp.TaskID}`" @click="showSureModal(ufp,'sure-modal-reset-followups', index)" class="la la-refresh text-primary ml-1" style="cursor:pointer;"></i>
                    <b-tooltip :target="`followup-reset-${ufp.TaskID}`" placement="top" triggers="hover">Restart Follow-up Cycle</b-tooltip>
                    
                    <i v-if="ufp.pastDue === 'true'" :id="`followup-action-plan-${ufp.TaskID}`" class="fa fa-circle text-danger ml-1"></i>
                    <b-tooltip :target="`followup-action-plan-${ufp.TaskID}`" placement="top" triggers="hover">Past Due!</b-tooltip>
                    
                    <i v-if="ufp.Overridden === 1" :id="`overridden-${ufp.TaskID}`" class="fa fa-exclamation text-warning ml-1"></i>
                    <b-tooltip :target="`overridden-${ufp.TaskID}`" placement="top" triggers="hover">Date was overridden</b-tooltip>
                    
                    <i v-if="ufp.InitialFollowupNote" :id="`initialfunote-${ufp.TaskID}`" class="glyphicon glyphicon-list-alt text-muted ml-1"></i>
                    <b-tooltip :target="`initialfunote-${ufp.TaskID}`" placement="top" triggers="hover">{{ ufp.InitialFollowupNote }}</b-tooltip>
                  </span>
                </td>
                
                <!-- ATTEMPTS -->
                <td>
                  <div v-if="ufp.Attempts && ufp.AttemptDetails" class="attempts-icons" :id="`attempt-${ufp.TaskID}`">
                    <!-- Show first 3 attempt icons -->
                    <span v-for="(attempt, aIndex) in ufp.AttemptDetails.split(',').slice(0, 3)" :key="`attempt-${aIndex}`">
                      <i v-if="attempt === 'P'" class="fa fa-phone text-success mr-1"></i>
                      <i v-else-if="attempt === 'E'" class="fa fa-envelope text-success mr-1"></i>
                      <i v-else class="fa fa-mobile text-success mr-1"></i>
                    </span>
                    <!-- Show +N indicator if more than 3 attempts -->
                    <span v-if="ufp.AttemptDetails.split(',').length > 3" class="attempts-more-indicator">
                      +{{ ufp.AttemptDetails.split(',').length - 3 }}
                    </span>
                    <b-tooltip :target="`attempt-${ufp.TaskID}`" placement="left" triggers="hover">
                      {{ ufp.Attempts }} attempt(s) made so far
                    </b-tooltip>
                  </div>
                  <span v-else class="glyphicon glyphicon-ban-circle text-danger" :id="`no-attempt-${ufp.TaskID}`">
                    <b-tooltip :target="`no-attempt-${ufp.TaskID}`" placement="left" triggers="hover">
                      No attempts made yet
                    </b-tooltip>
                  </span>
                </td>
                
                <!-- ACTION -->
                <td class="text-right">
                  <i class="fa fa-spinner fa-spin" style="font-size:16px;" v-if="loadingStatus[index]"/>
                  <a v-else href="#" @click="createFollowup($event, ufp, index)">
                    <b-button variant="default" size="sm" class="followup">
                      <i class="fa fa-phone text-success mr-xs"></i>
                      <small class="fw-semi-bold">Follow-up</small>
                    </b-button>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </template>

    <!-- Tasks tagged with this patient (2026-09-02) -->
    <PatientTasksCard v-if="patientData && siteID" :site-id="siteID" :pat-num="patientNumInt" />

    <!-- Treatment Plans Signed — not paid for (TX_PRESENTER_ENABLED off): blurred preview upsell -->
    <div v-if="allSettings && !canAccessTxPresenter" class="task-details-item" data-test="tx-signed-upsell">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-pencil-square-o section-icon"></i>
          Treatment Plans Signed
        </h5>
      </div>
      <div class="section-content">
        <FeaturePreview
          title="Enable Treatment Presenter"
          description="Send treatment plans to patients and collect their signatures online"
          icon="fa fa-pencil-square-o"
          :columns="['Treatment Plan', 'Signed On', 'Consent Forms']"
          :sample-data="[
            ['Crown and Root Canal', '2 days ago', '2 signed'],
            ['Implant Placement', '1 week ago', '1 signed'],
            ['Periodontal Therapy', '3 weeks ago', '1 signed']
          ]"
          row-icon="fa fa-pencil-square-o"
          feature-key="TX_PRESENTER"
          feature-label="Treatment Presenter"
          source-page="Patient Profile - Treatment Plans Signed"
        />
      </div>
    </div>

    <!-- Treatment Plans Signed (Tx Presenter) -->
    <div v-if="canAccessTxPresenter" class="task-details-item tx-signed-section">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-pencil-square-o section-icon"></i>
          Treatment Plans Signed
        </h5>
      </div>
      <div class="section-content">
        <v-client-table v-if="signedPlans.length > 0" :data="signedPlans" :columns="signedPlanColumns" :options="signedPlanOptions">
          <div slot="h__Plan">Treatment Plan</div>
          <div slot="h__SignedAt">Signed On</div>
          <div slot="h__Consents">Consent Forms</div>

          <!--
            Plan name. Three states, driven by the summary's pdf fields:
              • complete (pdfId non-null) → clickable link to the signed PDF
              • processing & in-flight    → plain text + muted spinner ("really is rendering")
              • stuck or failed           → amber warning + inline Retry
            An older cached payload has none of the new fields; the strict
            `=== true` / `=== 'failed'` checks make that degrade to plain text.
          -->
          <div slot="Plan" slot-scope="props">
            <div class="text-title-wrap">
              <i class="fa fa-pencil-square-o" aria-hidden="true"></i>
              <div
                v-if="props.row.pdfId !== null"
                class="tx-plan-link"
                :class="{ 'is-opening': openingPlanPdf[props.row.treatPlanNum] }"
                style="margin-left: 10px; padding-top: 3px; font-weight: lighter !important;"
                @click="onOpenSignedPlanPdf(props.row)"
              >
                {{ props.row.heading || formatDate(props.row.dateTP, allSettings.GENERAL_TIMEZONE.PropertyValue) }}
                <span v-if="openingPlanPdf[props.row.treatPlanNum]" class="tx-pdf-opening">
                  <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Opening…
                </span>
              </div>
              <div v-else style="margin-left: 10px; padding-top: 3px; font-weight: lighter !important;">
                <span class="tx-plan-name-text">
                  {{ props.row.heading || formatDate(props.row.dateTP, allSettings.GENERAL_TIMEZONE.PropertyValue) }}
                </span>

                <!-- Stuck / failed → honest warning + Retry -->
                <template v-if="isSignedPdfBroken(props.row)">
                  <i
                    :id="`tx-pdf-warn-${props.row.treatPlanNum}`"
                    class="fa fa-exclamation-triangle tx-pdf-warn"
                    aria-hidden="true"
                  ></i>
                  <b-tooltip :target="`tx-pdf-warn-${props.row.treatPlanNum}`" placement="top" triggers="hover">
                    The signed PDF didn't generate.
                  </b-tooltip>
                  <span v-if="retryingPlanPdf[props.row.treatPlanNum]" class="tx-pdf-retrying">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Retrying…
                  </span>
                  <button
                    v-else-if="props.row.pdfRetryPdfId !== null && props.row.pdfRetryPdfId !== undefined"
                    type="button"
                    class="tx-pdf-retry-btn"
                    @click="onRetrySignedPlanPdf(props.row)"
                  >
                    <i class="fa fa-refresh" aria-hidden="true"></i> Retry
                  </button>
                </template>

                <!-- Genuinely in flight (normally ~1-2s) -->
                <template v-else-if="props.row.pdfStatus === 'processing'">
                  <i
                    :id="`tx-pdf-proc-${props.row.treatPlanNum}`"
                    class="fa fa-circle-o-notch fa-spin tx-pdf-processing"
                    aria-hidden="true"
                  ></i>
                  <b-tooltip :target="`tx-pdf-proc-${props.row.treatPlanNum}`" placement="top" triggers="hover">
                    Generating the signed PDF…
                  </b-tooltip>
                </template>
              </div>
            </div>
          </div>

          <!-- Signed On -->
          <div slot="SignedAt" slot-scope="props">
            {{ formatDate(props.row.signedAt, allSettings.GENERAL_TIMEZONE.PropertyValue) }}
            <small v-if="props.row.signCount > 1" class="text-muted tx-sign-count">&times;{{ props.row.signCount }}</small>
          </div>

          <!-- Consent forms — one chip per bundled form -->
          <div slot="Consents" slot-scope="props">
            <div v-if="props.row.consentForms && props.row.consentForms.length > 0" class="tx-consent-chips">
              <span
                v-for="f in props.row.consentForms"
                :key="f.formIDForWeb"
                class="tx-consent-chip"
                :class="f.completed ? 'is-signed' : 'is-pending'"
                :title="f.completed ? ('Signed ' + formatDate(f.dateSubmitted, allSettings.GENERAL_TIMEZONE.PropertyValue)) : null"
                @click="f.completed && f.formSubmissionIDForWeb ? gotoViewSubmission(f.formSubmissionIDForWeb) : null"
              >
                <i v-if="f.completed" class="fa fa-check" aria-hidden="true"></i>
                <span class="tx-consent-chip-name">{{ f.name }}</span>
                <span v-if="!f.completed" class="tx-consent-chip-note">· not signed</span>
              </span>
            </div>
            <span v-else class="text-muted">No consent forms</span>
          </div>
        </v-client-table>

        <!-- Empty state message for no signed plans -->
        <div v-else class="no-form-submissions">
          <div class="empty-state-icon">
            <i class="fa fa-pencil-square-o"></i>
          </div>
          <p class="empty-state-message">
            No signed treatment plans for <strong>{{ patientData ? formatName(patientData.PatientName) : 'this patient' }}</strong>.
          </p>
          <p class="empty-state-submessage">
            Treatment plans signed through the Tx Presenter will appear here.
          </p>
        </div>
      </div>
    </div>

    <!--
      Insurance (2026-08-17). Primary/Secondary with benefits and a per-plan Verify, so a
      patient can be verified from their own profile — the dashboard only lists patients
      with an appointment in its window, so a completed visit used to leave no way to do it
      at all.
      ⛔ Gated on canUseInsurancePanel (flag AND CASL), NOT canAccessInsuranceVerification —
      this card carries a spend button. See the computed for why.
    -->
    <div ref="insurancePanel">
      <!-- Not paid for (INSURANCE_VERIFICATION_ENABLED off): blurred preview upsell. No Verify
           button here — the preview is inert, it can never run a billed check. -->
      <div v-if="allSettings && !insuranceVerificationEnabled" class="task-details-item" data-test="insurance-upsell">
        <div class="task-details-item-header">
          <h5 class="section-title">
            <i class="fa fa-shield section-icon"></i>
            Insurance
          </h5>
        </div>
        <div class="section-content">
          <FeaturePreview
            title="Enable Insurance Verification"
            description="Check eligibility and remaining benefits with the carrier, right from the patient profile"
            icon="fa fa-shield"
            :columns="['Plan', 'Remaining Benefits', 'Last Verified']"
            :sample-data="[
              ['Primary Dental PPO', '$1,250', 'Today'],
              ['Secondary Dental Plan', '$800', 'Today']
            ]"
            row-icon="fa fa-shield"
            feature-key="INSURANCE_VERIFICATION"
            feature-label="Insurance Verification"
            source-page="Patient Profile - Insurance"
          />
        </div>
      </div>
      <InsurancePanel
        v-if="canUseInsurancePanel"
        :site-id="siteID"
        :pat-num="patientNum"
        :patient-label="patientData ? formatName(patientData.PatientName) : 'this patient'"
        :patient="insurancePatient"
        :timezone="allSettings && allSettings.GENERAL_TIMEZONE ? allSettings.GENERAL_TIMEZONE.PropertyValue : ''"
        :status="insuranceStatus"
        :loading="insuranceLoading"
        @verified="loadInsuranceStatus"
      />
    </div>

    <div v-if="allSettings" class="task-details-item">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-file-text-o section-icon"></i>
          Form Submissions
        </h5>
      </div>
      <div class="section-content">
        <!-- Blurred Preview Pattern for Forms Upsell -->
        <FeaturePreview
          v-if="!formsPaidFor"
          title="Enable Digital Forms"
          description="Send forms to patients via Email or SMS"
          icon="fa fa-file-text-o"
          :columns="['Name', 'Last Updated']"
          :sample-data="[
            ['New Patient Intake Form', '2 days ago'],
            ['Medical History Update', '1 week ago'],
            ['Insurance Information', '2 weeks ago']
          ]"
          row-icon="fa fa-file-text-o"
          feature-key="FORMS"
          feature-label="Digital Forms"
          source-page="Patient Profile - Form Submissions"
        />

        <div v-else>
          <v-client-table v-if="formSubmissions && formSubmissions.length > 0" :data="formSubmissions" :columns="formColumns" :options="options">
            <div slot="h__Name">Name</div>
            <div slot="h__DateUpdated">Last Updated</div>
            <div slot="Name" slot-scope="props">
              <div class="text-title-wrap">
                <i class="fa fa-file-text-o" aria-hidden="true"></i>
                <div style="margin-left: 10px; cursor: pointer; padding-top: 3px; font-weight: lighter !important;" @click="gotoViewSubmission(props.row.FormSubmissionIDForWeb)">
                  {{ props.row.Name }}
                </div>
              </div>
            </div>

            <div slot="DateUpdated" slot-scope="props">
              {{formatDate(props.row.DateUpdated,allSettings.GENERAL_TIMEZONE.PropertyValue)}}
            </div>
          </v-client-table>

          <!-- Empty state message for no form submissions -->
          <div v-else class="no-form-submissions">
            <div class="empty-state-icon">
              <i class="fa fa-file-text-o"></i>
            </div>
            <p class="empty-state-message">
              No form submissions for <strong>{{patientData ? formatName(patientData.PatientName) : 'this patient'}}</strong> are available at the moment.
            </p>
            <p class="empty-state-submessage">
              Form submissions will appear here once the patient completes and submits a form.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="commlog && allSettings" class="task-details-item">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-comments section-icon"></i>
          Communication Log
        </h5>
      </div>
      <div class="section-content">
        <div class="mt-3">
          <b-form-group class="abc-radio">
            <b-form-radio-group
              id="commlogoptions"
              v-model="commLogSelectedMode"
              :options="commLogModeOptions"
              name="Mode_"
            ></b-form-radio-group>
          </b-form-group>
        </div>
        <v-client-table v-if="commlog" :data="commlog" :columns="commLogColumns" :options="commLogOptions">
          <div slot="h__CommDateTime">DATE & TIME</div>
          <div slot="h__Note">NOTE</div>
          <div slot="h__Mode_">TYPE</div>
          <div slot="h__UserName">USER</div>
          <div slot="CommDateTime" slot-scope="props">{{formatCommLogDateTime(props.row.CommDateTime)}}</div>
          <div slot="Note" slot-scope="props">
            <truncate
              collapsed-text-class="collapsed"
              clamp="More"
              :length="90"
              less="Show Less"
              type="html"
              :text="props.row.Note">
            </truncate>
          </div>
          <div slot="Mode_" slot-scope="props">
            <span v-if="props.row.Mode_ === 5" class="badge text-black badge-gray">Text</span>
            <span v-if="props.row.Mode_ === 3" class="badge text-black-dark badge-warning">Phone</span>
            <span v-if="props.row.Mode_ === 1" class="badge badge-badge badge-success">Email</span>
            <span v-if="props.row.Mode_ === 2" class="badge badge-badge badge-success">Email</span>
            <span v-if="props.row.Mode_ === 4" class="badge badge-badge badge-success">Email</span>
            <span v-if="props.row.Mode_ === 6" class="badge badge-badge badge-success">Email</span>
          </div>
          <div slot="UserName" slot-scope="props">
            <small>{{props.row.UserName}}</small>
            <br />
          </div>
        </v-client-table>
      </div>
    </div>    

    <!-- FAMILY PANEL START -->
    <div class="task-details-item" v-if="familyMembers && familyMembers.length > 0">
      <div class="task-details-item-header">
        <h5 class="section-title">
          <i class="fa fa-users section-icon"></i>
          Family Members
        </h5>
      </div>
      <div class="section-content">
        <div class="family-section">
        <div class="card panel" v-for="(member, index) in familyMembers" :key="index">
          <div role="button" class="card-header panel-header accordion-toggle cursor-pointer" @click="toggleBlock(index, member.PatNum)">
            <h6 class="text-success fw-semi-bold mr-3"><a role="button" class="">{{formatName(member.PatientName)}}</a></h6>
            <span class="fw-normal mr-3 mb-2"><small>{{member.Age}}{{ member.Gender === 0 ? 'M' : 'F' }}</small></span>
            <span class="badge text-gray-dark badge-warning" v-if="member.Guarantor === member.PatNum">GUARANTOR</span>
            <i :class="`ml-auto fa fa-angle-down ${member.isShown ? 'expanded' : ''}`"></i>
          </div>
          <b-collapse id="accordion-second" class="panel-body" :visible="isShown[index]">
            <span>
              <router-link :to="{ name: 'patientprofile', params: {patientID:  `${siteID}-${member.PatNum}`} }">
                  <small>View Profile</small>
              </router-link>
            </span>
            <div class="card-body mb-4"> 
              <h6 class="text-success fw-semi-bold">Recall Appointment:
                <b-badge variant="success" class="text-white" pill v-if="queryResults[index+1] && queryResults[index+1].data[1][0] && queryResults[index+1].data[1][0].RecallScheduled !== null">Scheduled</b-badge>
                <b-badge variant="warning" class="text-white" pill v-if="queryResults[index+1] && queryResults[index+1].data[1][0] && queryResults[index+1].data[1][0].RecallScheduled === null">Not Scheduled</b-badge>
              </h6>
              <ul class="comunication-panel" v-if="queryResults[index+1] && queryResults[index+1].data[1][0] && queryResults[index+1].data[1][0].RecallScheduled !== null">
                <li>
                <i class="fa fa-calendar fa-fw mr-1"/><small>{{queryResults[index+1].data[1][0].RecallScheduled | dateformat(dateFormatStr)}}</small></li>
              </ul>
              
            </div>
            <div class="card-body mb-4"> 
              <h6 class="text-success fw-semi-bold">Scheduled Appointment(s):
                <b-badge variant="warning" class="text-white" pill v-if="queryResults[index+1] && queryResults[index+1].data[2].length === 0">No Scheduled appointments</b-badge>
              </h6>
                <div class="table-resposive" v-if="queryResults[index+1] && queryResults[index+1].data[2].length > 0">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Procedure</th>
                      <th>Provider</th>
                      <th>Date & Time</th>
                      <th>Appointment Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in queryResults[index+1].data[2]" :key="index">
                      <td>{{appt.ProcDescript}}</td>
                      <td>{{appt.Provider}}</td>
                      <td>{{appt.AptDateTime | dateformatwithtime(allSettings.GENERAL_TIMEZONE.PropertyValue)}}</td>
                      <td>{{appt.AppointmentNotes}}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="card-body mb-4">
              <h6 class="text-success fw-semi-bold">Unscheduled Treatment in Treatment Plan:</h6>
              <v-client-table v-if="queryResults[index+1] && queryResults[index+1].data[3]" :data="queryResults[index+1].data[3]" :columns="unscheduledTreatmentColumns" :options="options">
                <div slot="h__Priority">Priority</div>
                <div slot="h__ProcCode">Procedure</div>
                <div slot="h__ToothNum">Tooth/Surf</div>
                <div slot="h__Descript">Description</div>
                <div slot="h__Provider">Provider</div>
                <div slot="h__DateTP">Tx Plan Date</div>

                <div slot="ToothNum" slot-scope="props">
                  <span v-if="getCountry === 'CANADA'"><small>{{convertUniversalToFDI(props.row.ToothNum)}} </small></span>
                  <span  v-else><small>{{props.row.ToothNum}}</small>
                    <span v-if="props.row.Surf !== ''">-</span>{{props.row.Surf}}</span>
                </div>

                <div slot="DateTP" slot-scope="props">
                  <span>{{props.row.DateTP | dateformat(dateFormatStr)}}</span>
                </div>
              </v-client-table>
            </div>
          <div class="card-body"> 
            <h6 class="text-danger fw-semi-bold mb-4">Broken Appointment(s) History:</h6>
            <div role="alert" aria-live="polite" aria-atomic="true" class="alert alert-dismissible alert-success" v-if="queryResults[index+1] && queryResults[index+1].data[4].length === 0">
              <span class="fw-semi-bold"><span class="glyphicon glyphicon-thumbs-up"></span> No history of broken appointment(s)</span>
            </div>
            <div class="table-resposive" v-if="queryResults[index+1] && queryResults[index+1].data[4].length > 0">
              <table class="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Procedure code / Adjustment</th>
                    <th>Provider</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(appt, index) in queryResults[index+1].data[4]" :key="index">
                    <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                    <td><span class="badge text-black badge-gray badge-pill">{{appt.AdjustProcCode}}</span></td>
                    <td>{{appt.Provider}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </b-collapse>
          </div>
        </div>
      </div>
    </div>
        <!-- FAMILY PANEL END -->
      </b-col>
    </b-row>
    
    <!-- Patient SMS Widget -->
    <PatientSmsWidget 
      v-if="patientData && patientData.PatNum"
      :patient="patientData"
    />
    
    <b-modal ref="sure-modal-remove-initial-followup-date" size="sm" body-class="bg-white" title="Are you sure?" @ok="runSureOk('REMOVE-INITIAL-FOLLOWUP-DATE')" @cancel="hideSureModal('sure-modal-remove-initial-followup-date')">
        Are you sure you want to delete this Initial Follow-up?
    </b-modal>

    <b-modal ref="sure-modal-stop-followups" size="sm" body-class="bg-white" title="Are you sure?" @ok="runSureOk('STOP-FOLLOWUPS')" @cancel="hideSureModal('sure-modal-stop-followups')">
        Are you sure you want to stop future Follow-ups?
    </b-modal>
    <b-modal ref="sure-modal-reset-followups" size="sm" body-class="bg-white" title="Are you sure?" @ok="runSureOk('RESET-FOLLOWUPS')" @cancel="hideSureModal('sure-modal-reset-followups')">
       Are you sure you want to reset the Follow-up Cycle and start following up again?
    </b-modal>

    <!-- AI Call Result Modal (live streaming) -->
    <b-modal v-if="isSuperAdmin" v-model="showAICallResultModal" size="lg" ok-only :ok-title="aiCallInProgress ? 'Running...' : 'Close'" :ok-disabled="aiCallInProgress">
      <template #modal-title>
        <span v-if="aiCallInProgress"><i class="fa fa-phone fa-flip-horizontal text-success mr-2"></i> AI Call in progress...</span>
        <span v-else>AI Call Result</span>
      </template>

      <div v-if="aiCallPersona" class="mb-3">
        <small class="text-muted">
          Patient persona: <b-badge variant="info">{{ aiCallPersona.scenario }}</b-badge>
          prefers {{ aiCallPersona.preferredContact }} |
          {{ aiCallPersona.verbosity }}
        </small>
      </div>

      <div v-if="aiCallResult && aiCallResult.outcome">
        <p>
          <strong>Outcome:</strong>
          <b-badge :variant="aiCallResult.outcome === 'booked' ? 'success' : 'secondary'">{{ aiCallResult.outcome }}</b-badge>
          <strong class="ml-3">Mode:</strong> {{ aiCallResult.mode }}
          <strong class="ml-3">Duration:</strong> {{ aiCallResult.durationSeconds }}s
        </p>
        <hr/>
      </div>

      <div v-if="aiCallLiveTranscript.length > 0 || aiCallInProgress">
        <h6>Transcript</h6>
        <div v-for="(t, i) in aiCallLiveTranscript" :key="i" class="mb-2 ai-turn" :class="'ai-turn-' + t.role">
          <small class="text-muted">{{ t.role === 'agent' ? '🤖 Agent (Aria)' : t.role === 'patient' ? '👤 Patient' : t.role }}</small><br/>
          <span :class="t.role === 'agent' ? 'text-primary' : ''">{{ t.message }}</span>
        </div>
        <div v-if="aiCallInProgress" class="text-muted">
          <i class="fa fa-spinner fa-spin"></i> Waiting for next turn...
        </div>
      </div>

      <div v-else-if="!aiCallResult || !aiCallResult.outcome">
        <p class="text-muted"><i class="fa fa-spinner fa-spin"></i> Starting simulation...</p>
      </div>

      <div v-else-if="aiCallResult && !aiCallResult.success">
        <p class="text-warning">
          <i class="fa fa-warning"></i>
          {{ aiCallResult.message || 'Call did not complete.' }}
        </p>
      </div>
    </b-modal>

    <slideout-panel ref="sidebar"></slideout-panel>

  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Widget from '@/components/Widget/Widget';
import Multiselect from "vue-multiselect";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import DatePicker from "vue2-datepicker";
import mixin from '../../mixin.js';
import { isEmailEnabled, isSmsEnabled, hasSmsNumber, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js';
import { isTextablePhone, describePhoneProblem } from '@/utils/patientPhone.js';
import { getTextBlockReason, isTextBlocked, TEXT_BLOCK_CODES } from '@/utils/textConsent.js';
import CreateFollowup from '../Admin/components/CreateFollowup';
import PatientSidebar from './components/PatientSidebar';
import PatientSmsWidget from './components/PatientSmsWidget';
import GetFormQRModal from './components/GetFormQRModal';
// Slice 11 Phase C — Tx Plans dispatcher (Super Admin gated entry into the
// Tx Presenter Send flow from Patient Profile).
import PatientTxPlansModal from '@/pages/TxPresenter/components/PatientTxPlansModal.vue';
import SendPlanModal from '@/pages/TxPresenter/components/SendPlanModal.vue';
import truncate from 'vue-truncate-collapsed';
import FeaturePreview from '@/components/FeaturePreview';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
import PatientProfileSkeleton from './PatientProfileSkeleton.vue';
import CollectPaymentModal from './components/CollectPaymentModal.vue';
import ReviewRequestModal from './components/ReviewRequestModal.vue';
import FormUpdateBadge from './components/FormUpdateBadge.vue';
import InsuranceStatusChip from '@/pages/InsuranceVerification/components/InsuranceStatusChip.vue';
import InsurancePanel from './components/InsurancePanel.vue';
import CreateTaskPanel from './components/CreateTaskPanel.vue';
import PatientTasksCard from './components/PatientTasksCard.vue';
import PatientPhotoAvatar from './components/PatientPhotoAvatar.vue';
import PatientOdImageUpload from './components/PatientOdImageUpload.vue';
import { triggerAutorecallCall } from '@/api/autorecall';
import { parseSendNewLinkQuery } from '@/utils/actionCenterPaymentLink';
// Follow-ups redesign (Phase 5) — the new Follow-ups section. See fuProfileNewUi. Loaded on first
// render (Super Admins only today): its follow-up actions import ToastComponent → @/store → the
// router, which would otherwise ride along with every import of this page (component specs too).
const FollowupsProfileSection = () => import('@/components/Followups/FollowupsProfileSection.vue');
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, setFollowupsUiChoice, browserStorage } from '@/utils/followups/uiVersion.js';
import { profileFactsFrom } from '@/utils/followups/rowModel.js';
import { scheduleSendCatalogPrefetch } from '@/utils/sendPanelCatalog.js';

export default {
  name: 'PatientProfile',
  components: { DatePicker, Widget, truncate, Multiselect, ErrorDisplay, PatientSmsWidget, FeaturePreview, FeatureRequestButton, FeatureRequestNote, PatientProfileSkeleton, GetFormQRModal, PatientTxPlansModal, SendPlanModal, CollectPaymentModal, ReviewRequestModal, FormUpdateBadge, InsuranceStatusChip, InsurancePanel, PatientTasksCard, PatientPhotoAvatar, PatientOdImageUpload, FollowupsProfileSection, FollowupsUiToggle },
  props: ['patientID'],
  mixins: [mixin],
  mounted() {
    // PHASE 2 PREVENTION: Add browser event listeners for cleanup
    this.setupBrowserEventListeners();
    scheduleSendCatalogPrefetch(this.$store, this.siteID); // silent, delayed: the Send Form/Text/Email lists, so the first click is instant
  },

  created() {
    // VALIDATION: Enforce strict format UUID-PatNum (e.g. 550e8400-e29b-41d4-a716-446655440000-12345)
    // Regex breakdown:
    // ^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}  matches standard UUID (36 chars)
    // -                                                                             matches separator
    // \d+$                                                                          matches one or more digits at end
    const validFormatRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-\d+$/;

    let patNumLocation = this.patientID ? this.patientID.indexOf('PatNum') : -1;

    // If it contains "PatNum", it's the special legacy case handled below. 
    // If it DOES NOT contain PatNum, it MUST match the validFormatRegex.
    if (this.patientID && patNumLocation === -1 && !validFormatRegex.test(this.patientID)) {
      const errorMsg = `Invalid Patient Link Format: The ID provided (${this.patientID}) does not match the expected format (SiteUUID-PatientNumber). Please check your Open Dental Program Link configuration.`;
      console.error(errorMsg);
      this.$toast.error(errorMsg, { timeout: 10000 });
      this.$router.push('/app/');
      return;
    }

    // if there's a string like [patNum] in the patientID then that means that someone clicked a button in Open Dental without selecting a patient.
    if(this.patientID && patNumLocation === -1) {
      if(this.allSettings) {
        //console.log('now calling init');
        this.init();
      }
    } else {
      console.log('patNum empty. Now redirecting...');
      this.$router.push('/app/');
    }
  },

  // PHASE 2 PREVENTION: Add beforeDestroy lifecycle hook for cleanup
  beforeDestroy() {
    this.cleanupOnDestroy();
  },

  // PHASE 2 PREVENTION: Vue 3 compatibility
  beforeUnmount() {
    this.cleanupOnDestroy();
  },

  data(){
    return {
      notBefore: moment(),
      // Insurance Verification status chip (6b). null until the soft-gated
      // patient-status endpoint answers; { enabled:false } → chip stays hidden.
      insuranceStatus: null,
      insuranceLoading: false,
      teamioPatientID: null,
      lastFollowedUpWithTeamioPatientID: null,
      loadingStatus: [].repeat(false, 5),
      newFollowupDates: [].repeat(null, 5),
      followupActionPlanLoading: {},
      followupDateChangeInProgress: [].repeat(false, 5),
      followupDateChangeInProgress: [].repeat(false, 5),
      initialFollowupDate: new Date(),
      initialFollowupNote: '',
      showHelpPopOver: false,
      showSendFormHelpPopOver: false,
      showSendSmsHelpPopOver: false,
      showSendEmailHelpPopOver: false,
      aiCallInProgress: false,
      aiCallResult: null,
      aiCallLiveTranscript: [],
      aiCallPersona: null,
      showAICallResultModal: false,
      showGetFormQRModal: false,
      showCollectPaymentModal: false,
      // Resolved TEXT_TO_PAY_SETTINGS_PAYMENT, fetched fresh each time the
      // Collect Payment modal is opened (see onOpenCollectPayment).
      collectPaymentTextToPaySettings: null,
      collectPaymentOpening: false,
      // Action Center "Payment failed → Send new link" opens the dialog pre-filled via
      // ?collectPayment=1&amount=…&purpose=… (src/utils/actionCenterPaymentLink.js). Once per page instance.
      collectPaymentPrefill: null,
      collectPaymentDeepLinkHandled: false,
      showReviewRequestModal: false,
      showAddToAsapHelpPopOver: false,
      showUpdateAsapHelpPopOver: false,
      followupType: 0,
      results: [],
      isShown: [false, false, false, false, false, false],
      familyMembersDetails: {},
      familyMemberAppointments: [{
        name:'Nicole Adams',
        isGuarantor: true,
        isShown: false
      }, {
        name:'Eileen Adams',
        isShown: false
      }],
      unscheduledTreatmentColumns: ['Priority','ProcCode', 'ToothNum', 'Descript', 'Provider', 'DateTP'],
      asapApptListColumns: ['AptStatus', 'AppointmentDate', 'ProcDescript', 'Note'],
      commLogColumns: ["CommDateTime", "Note", "Mode_","UserName"],
      commLogOptions: {
        filterable: false,
        skin:'table',
        sortable: [],
        perPage: 5,
        sortByColumn: false,
        pagination: { 
          chunk: 5, 
          dropdown: false 
        }
      },
      commLogSelectedMode: null,
      commLogModeOptions: [
        
        {
          value: null,
          text: 'All'
        },
        {
          value: 100,
          text: 'Teamio'
        },
        {
          value: 1,
          text: 'Email'
        },
        {
          value: 5,
          text: 'Text'
        },
        {
          value: 2,
          text: 'Mail'
        },
        {
          value: 3,
          text: 'Phone'
        },
        {
          value: 4,
          text: 'In Person'
        },        
        {
          value: 6,
          text: 'Email And Text'
        }
    ],      
      columns: ['procedure code', 'description', 'provider'],
      formColumns: ['Name', 'DateUpdated'],
      data: [{'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'},
      {'procedure code':'D9986', description: 'Missed Appointment', provider: 'Dr. Shah'}],
      options: {
        filterable: false,
        skin:'table',
        sortable: [],
        perPage: 5,
        sortByColumn: false,
        pagination: { chunk: 5, dropdown: false }
      },
      // Tx Presenter — "Treatment Plans Signed" section table config.
      signedPlanColumns: ['Plan', 'SignedAt', 'Consents'],
      signedPlanOptions: {
        filterable: false,
        skin:'table',
        sortable: [],
        perPage: 5,
        sortByColumn: false,
        pagination: { chunk: 5, dropdown: false }
      },
      // treatPlanNum → true while that row's PDF retry is in flight (keeps the
      // row showing "Retrying…" until the re-fetch swaps in the finished link).
      retryingPlanPdf: {},
      // treatPlanNum → true while we're resolving that row's presigned PDF URL.
      // Opening isn't instant (a round-trip, and a cold Lambda can take a few
      // seconds), so the row says "Opening…" instead of looking inert and
      // inviting repeat clicks.
      openingPlanPdf: {},
      // Pending post-retry re-fetch timers, cleared on destroy so a 4s timer
      // can't outlive the page and re-fetch against another patient.
      signedPdfRetryTimers: [],
      initialFollowupInProgress: false,
      followupParams: null,
      followupIndex: null,
      textMsg: null,
      // PHASE 2 PREVENTION: Track active follow-up sessions for cleanup
      activeFollowupSessions: new Set(),
      // PHASE 2 PREVENTION: Store event listener references for removal
      browserEventListeners: {
        beforeUnload: null,
        visibilityChange: null,
        pagehide: null
      },
      // PHASE 2 PREVENTION: Timer for delayed cleanup
      cleanupTimer: null,
      // Follow-ups redesign (Phase 5): bumped by the Classic / New view links so fuProfileNewUi re-reads storage
      fuUiVersion: 0
    }
  },
  methods:{
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setLoading', 'setErrored', 'setMessage']),
    ...mapActions('teamio', ['runQuery', 'startTrackingFUAttempt', 'stopTrackingFUAttempt', 'sendTextMsg', 'getTextToPaySettings']),
    ...mapMutations('teamio', ['emptyResults']),
    ...mapActions('tasks', ['updateInitialFollowupDate', 'updateFollowupDate', 'removeInitialFollowupDate', 'stopFollowups', 'resetFollowups']),
    ...mapActions('txPresenter', ['openPatientTxPlansDispatcher', 'fetchSignedPlansSummary', 'fetchSignedPdfUrl', 'retrySignedPlanPdf']),
    ...mapActions('insuranceVerification', { fetchInsurancePatientStatus: 'fetchPatientStatus' }),

    // Insurance Verification chip (6b): load the soft-gated per-patient status.
    // Non-fatal — the profile must never break on this. When the feature flag is
    // off the backend returns { enabled:false } and the chip renders nothing.
    //
    // `opts.quiet` = the Insurance panel's free follow-up after a /verify that got no answer
    // (InsurancePanel makeVerifyFollowUp). Quiet reads don't flash "Loading…", and a read that
    // fails keeps the card as it was: the follow-up runs precisely when the network just
    // misbehaved, and blanking the card to "No insurance on file" mid-wait would be a lie.
    async loadInsuranceStatus(opts) {
      if (!this.canAccessInsuranceVerification) return;
      if (!this.siteID || !this.patientNum) return;
      const quiet = !!(opts && opts.quiet);
      // Reads are now scheduled (the follow-up), so one can resolve after the profile has
      // moved to another patient. Never paint patient A's plans under patient B.
      const patNum = this.patientNum;
      if (!quiet) this.insuranceLoading = true;
      try {
        const data = await this.fetchInsurancePatientStatus({ siteID: this.siteID, patNum });
        if (patNum !== this.patientNum) return;
        if (quiet && !data) return;
        this.insuranceStatus = data && data.enabled ? data : null;
      } catch (err) {
        console.error('[PatientProfile] loadInsuranceStatus error:', err);
        if (patNum === this.patientNum && !quiet) this.insuranceStatus = null;
      } finally {
        if (!quiet) this.insuranceLoading = false;
      }
    },

    /**
     * The chip's click target.
     *
     * ⛔ It used to always push to the Insurance Verification dashboard — which is exactly
     * the dead end that started this work: the dashboard lists patients with an appointment
     * in its window, so clicking the chip for a patient whose visit had finished landed on
     * a page that did not contain them, with no way back to this patient.
     *
     * When the panel is on the page, scroll to it instead: the answer is right here. The
     * router push survives only for the case where the panel is NOT rendered (a Super Admin
     * on a site without the flag, who passes the chip's looser gate but not the panel's).
     */
    goToInsuranceVerification() {
      if (this.canUseInsurancePanel) {
        const el = this.$refs.insurancePanel;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
      }
      if (this.$route.path !== '/app/insurance-verification') {
        this.$router.push('/app/insurance-verification');
      }
    },

    /**
     * Slice 11 Phase C — open the Tx Plans dispatcher from Patient Profile.
     * The dispatcher loads the patient's saved + active plans via
     * /get-tx-presentation-detail, then renders a picker. When the TC selects
     * a plan, the store's selectPatientPlanForSend action loads the Builder
     * context + opens SendPlanModal — so this entry point ends up in the same
     * state as opening Send from inside the Builder.
     */
    onOpenTxPlans() {
      if (!this.siteID) {
        this.$toast.error('No site selected — refresh and try again.');
        return;
      }
      if (!this.patientNum) {
        this.$toast.error('Could not determine patient — refresh and try again.');
        return;
      }
      this.openPatientTxPlansDispatcher({ siteID: this.siteID, patNum: this.patientNum });
    },

    async onOpenCollectPayment() {
      if (this.collectPaymentOpening) return;
      if (!this.siteID || !this.patientNum) {
        this.$toast.error('Could not determine site or patient — refresh and try again.');
        return;
      }
      if (!this.hasPaymentProvider) {
        this.$toast.error('No payment provider configured for this site. Set one up in Payment Settings first.');
        return;
      }

      // Resolve TEXT_TO_PAY_SETTINGS_PAYMENT BEFORE opening. The modal decides
      // its default purpose in onOpen() from whether prepaymentUnearnedType is
      // configured, so the answer has to be settled by then — fetching from
      // inside the modal would let the "Treatment plan deposit" card flip from
      // disabled to enabled after the defaults were already computed.
      // Re-fetched per open (one cheap GET) so an office manager who just
      // configured the setting doesn't hit a stale disabled card.
      this.collectPaymentOpening = true;
      try {
        const res = await this.getTextToPaySettings(this.siteID);
        this.collectPaymentTextToPaySettings = (res && res.settings) || {};
      } catch (err) {
        // Non-fatal: an empty object just means deposits stay unavailable, and
        // balance collection — the common case — is unaffected.
        console.error('[PatientProfile] getTextToPaySettings error:', err);
        this.collectPaymentTextToPaySettings = {};
      } finally {
        this.collectPaymentOpening = false;
      }

      this.showCollectPaymentModal = true;
    },

    // Action Center "Payment failed → Send new link" lands here with ?collectPayment=1. Open the dialog
    // pre-filled once the patient has loaded; the operator reviews and sends from it. The query stays in
    // the URL (router-view is keyed by fullPath — replacing it would remount the page), and the handled
    // flag keeps it to one open per visit.
    maybeOpenCollectPaymentFromQuery() {
      if (this.collectPaymentDeepLinkHandled) return;
      const prefill = parseSendNewLinkQuery(this.$route && this.$route.query);
      if (!prefill || !this.patientData || !this.siteID || !this.patientNum) return;
      this.collectPaymentDeepLinkHandled = true;
      this.collectPaymentPrefill = prefill;
      this.onOpenCollectPayment();
    },

    onOpenReviewRequest() {
      if (!this.siteID || !this.patientNum) {
        this.$toast.error('Could not determine site or patient — refresh and try again.');
        return;
      }
      this.showReviewRequestModal = true;
    },

    // Format date for quick stats display
    formatDateShort(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    // Format currency for balance display
    formatCurrency(amount) {
      if (amount === undefined || amount === null) return '$0.00';
      const currencySymbol = this.allSettings?.GENERAL_CURRENCY_SYMBOL?.PropertyValue || '$';
      return `${currencySymbol}${parseFloat(amount).toFixed(2)}`;
    },

    // Follow-ups redesign (Phase 5): the Super Admin "Classic view" / "New view" links.
    fuSetUi(target) {
      setFollowupsUiChoice(browserStorage(), target);
      this.fuUiVersion += 1;
    },

    // Follow-up helper - check if can reset follow-up cycle
    canResetFollowup(ufp) {
      return (ufp.TeamioFollowupStatusID === 3 || ufp.FollowupStopped === 1) || 
             (ufp.FollowupDate === null && ufp.Attempts > 0 && ufp.FollowupStopped === 0);
    },

    showSureModal(ufp, modal, index) {
      this.followupParams = ufp;
      this.followupIndex = index;
      this.$refs[modal].show();
    },

    toggleBlock(index) {
        //console.log(`index: ${index}`);
        Vue.set(this.isShown, index, !this.isShown[index]);
      },

    hideSureModal(ref) {
      this.$refs[ref].hide();
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    // PHASE 2 PREVENTION: Setup browser event listeners for cleanup
    setupBrowserEventListeners() {
      // Handle browser refresh/close
      this.browserEventListeners.beforeUnload = (event) => {
        this.performEmergencyCleanup();
      };
      window.addEventListener('beforeunload', this.browserEventListeners.beforeUnload);

      // Handle tab visibility changes (user switches tabs, minimizes browser)
      this.browserEventListeners.visibilityChange = () => {
        if (document.hidden) {
          this.startCleanupTimer();
        } else {
          this.cancelCleanupTimer();
        }
      };
      document.addEventListener('visibilitychange', this.browserEventListeners.visibilityChange);

      // Handle page unload (mobile Safari, some browsers)
      this.browserEventListeners.pagehide = () => {
        this.performEmergencyCleanup();
      };
      window.addEventListener('pagehide', this.browserEventListeners.pagehide);
    },

    // PHASE 2 PREVENTION: Start cleanup timer when page is hidden
    startCleanupTimer() {
      // Clear any existing timer
      this.cancelCleanupTimer();

      // Start 5-minute timer for cleanup if page stays hidden
      this.cleanupTimer = setTimeout(() => {
        this.performEmergencyCleanup();
      }, 5 * 60 * 1000); // 5 minutes
    },

    // PHASE 2 PREVENTION: Cancel cleanup timer
    cancelCleanupTimer() {
      if (this.cleanupTimer) {
        clearTimeout(this.cleanupTimer);
        this.cleanupTimer = null;
      }
    },

    // PHASE 2 PREVENTION: Perform emergency cleanup of all active sessions
    async performEmergencyCleanup() {
      if (this.activeFollowupSessions.size === 0) {
        return;
      }

      const cleanupPromises = Array.from(this.activeFollowupSessions).map(async (patientId) => {
        try {
          const payload = {
            cacheName: process.env.VUE_APP_MOMENTO_CACHE,
            cacheSet: 'fuattempts',
            element: patientId
          };
          await this.stopTrackingFUAttempt(payload);
        } catch (error) {
          console.error(`Follow-up cleanup failed for patient ${patientId}:`, error);
        }
      });

      await Promise.allSettled(cleanupPromises);
      this.activeFollowupSessions.clear();
    },

    // PHASE 2 PREVENTION: Component cleanup on destroy
    async cleanupOnDestroy() {
      // Remove browser event listeners
      if (this.browserEventListeners.beforeUnload) {
        window.removeEventListener('beforeunload', this.browserEventListeners.beforeUnload);
      }
      if (this.browserEventListeners.visibilityChange) {
        document.removeEventListener('visibilitychange', this.browserEventListeners.visibilityChange);
      }
      if (this.browserEventListeners.pagehide) {
        window.removeEventListener('pagehide', this.browserEventListeners.pagehide);
      }

      // Cancel any pending cleanup timer
      this.cancelCleanupTimer();

      // Drop pending signed-PDF retry re-fetches
      this.signedPdfRetryTimers.forEach((t) => clearTimeout(t));
      this.signedPdfRetryTimers = [];

      // Perform final cleanup of active sessions
      await this.performEmergencyCleanup();
    },

    /*
      Any change made below to query on line 715 should be added to query on line 727 and 1054 as well
    */
  
    async init() {
      let query = {
        type: 'PATIENT_PROFILE',
        siteID: this.siteID,
        patNum: this.patientNum,
        _date: moment().format('YYYY-MM-DD'),
        country: localStorage.getItem('country') || 'USA'
      };

      this.emptyResults();
      this.results = await this.runQuery(query);

      // Tx Presenter — "Treatment Plans Signed" section. Fire-and-forget so it
      // never blocks the main profile render; the section renders its own
      // loading/empty states off the store and the fetch is non-fatal.
      if (this.canAccessTxPresenter) {
        this.fetchSignedPlansSummary({ siteID: this.siteID, patNum: this.patientNum })
          .catch((err) => console.error('[PatientProfile] fetchSignedPlansSummary error:', err));
      }

      // Insurance Verification status chip — same fire-and-forget, non-blocking
      // pattern as the signed-plans fetch. Soft-gated: renders nothing when off.
      this.insuranceStatus = null;
      this.loadInsuranceStatus();

      if(this.familyMembers && this.familyMembers.length > 0) {
        for (const fm of this.familyMembers) {
          //console.log(`now fetching data for ${fm.PatNum}`);
          let query = {
            type: 'PATIENT_PROFILE',
            siteID: this.siteID,
            patNum: fm.PatNum,
            _date: moment().format('YYYY-MM-DD'),
            country: localStorage.getItem('country') || 'USA'
          };
          this.familyMembersDetails[fm.PatNum] = await this.runQuery(query);
          //console.log('done');
        }
      }
    },

    // Open the shared TaskComposer in a right slide-out, pre-seeded with this
    // patient. Deliberately NOT role-gated (Saumil: anyone can create a task).
    onCreateTask() {
      if (!this.patientData) return;
      const { PatNum, LName, FName } = this.patientData;
      this.$showPanel({
        component: CreateTaskPanel,
        openOn: 'right',
        width: 800,
        props: {
          siteID: this.siteID,
          patient: { PatNum, LName, FName },
        },
      });
    },

    async sendForm() {
      try {
        const panel1Handle = this.$showPanel({
            component : PatientSidebar,
            openOn: 'right',
            width: 750,
            props: {
              sidebarType: 'Form',
              patientData: this.patientData,
              canSendSMS: this.canSendSMS,
              canSendEmail: this.canSendEmail,
              smsFeatureEnabled: this.smsFeatureEnabled,
              emailFeatureEnabled: this.emailFeatureEnabled
            }
          })

          panel1Handle.promise
            .then(result => {
              console.log(`panel handle promise done`);            
          });
      } catch(error) {
        console.log(error);
        panel1Handle.hide();
      }

    },

    async sendText() {
      const panel2Handle = this.$showPanel({
          component : PatientSidebar,
          openOn: 'right',
          width: 750,
          props: {
            sidebarType: 'Text',
            patientData: this.patientData,
            canSendSMS: this.canSendSMS,
            canSendEmail: this.canSendEmail,
            smsFeatureEnabled: this.smsFeatureEnabled,
            emailFeatureEnabled: this.emailFeatureEnabled
          }
        })

        panel2Handle.promise
          .then(result => {
            //console.log(`pane2 handle promise done. Result: ${JSON.stringify(result)}`);          
        });
    },

    async sendEmail() {
      const panel2Handle = this.$showPanel({
          component : PatientSidebar,
          openOn: 'right',
          width: 750,
          props: {
            sidebarType: 'Email',
            patientData: this.patientData,
            canSendSMS: this.canSendSMS,
            canSendEmail: this.canSendEmail,
            smsFeatureEnabled: this.smsFeatureEnabled,
            emailFeatureEnabled: this.emailFeatureEnabled
          }
        })

        panel2Handle.promise
          .then(result => {
            //console.log(`pane2 handle promise done. Result: ${JSON.stringify(result)}`);
        });
    },

    getFormQR() {
      this.showGetFormQRModal = true;
    },

    // Photo to OD — opens the sheet; the component re-checks the gate and the mask.
    onOpenPhotoToOd() {
      if (this.$refs.photoToOd) this.$refs.photoToOd.open();
    },

    async onAICall() {
      if (!this.canAICall || this.aiCallInProgress) return;
      this.aiCallInProgress = true;

      // Reset live-streaming state
      this.aiCallResult = { transcript: [], outcome: null, mode: 'simulation', durationSeconds: 0 };
      this.aiCallLiveTranscript = [];
      this.aiCallPersona = null;
      this.showAICallResultModal = true;

      try {
        const result = await triggerAutorecallCall({
          siteID: this.siteID,
          patNum: parseInt(this.patientNum),
          taskID: `profile-${Date.now()}`,
          callReason: 'recall'
        }, (event) => {
          // Live SSE event handler — pushes turns into a reactive array
          if (event.type === 'simulation_started') {
            this.aiCallPersona = event.persona;
          } else if (event.type === 'turn') {
            this.aiCallLiveTranscript.push({
              role: event.role,
              message: event.message,
              timestamp: event.timestamp
            });
          } else if (event.type === 'simulation_complete') {
            // final transcript arrives in result.transcript; live array already has all turns
          }
        });

        this.aiCallResult = result;
        if (!result.success) {
          this.$toast.warning(result.message || 'AI call blocked: ' + result.reason);
        }
      } catch (err) {
        this.$toast.error('AI call failed: ' + (err.response?.data?.message || err.message));
      } finally {
        this.aiCallInProgress = false;
      }
    },

    async addToAsap() {
      const panel2Handle = this.$showPanel({
          component : PatientSidebar,
          openOn: 'right',
          width: 750,
          props: {
            sidebarType: 'ASAP',
            patientData: this.patientData
          }
        })

        panel2Handle.promise
          .then(result => {
            //console.log(`pane2 handle promise done. Result: ${JSON.stringify(result)}`);            
        });
    },

    async updateAsap() {
      const panel2Handle = this.$showPanel({
          component : PatientSidebar,
          openOn: 'right',
          width: 750,
          props: {
            sidebarType: 'ASAP',
            patientData: this.patientData,
            asapApptList: this.asapApptList
          }
        })

        panel2Handle.promise
          .then(result => {
            //console.log(`pane2 handle promise done. Result: ${JSON.stringify(result)}`);            
        });
    },



    async changeFollowupDate(event, index, newFollowupDate, ufp) {
      //console.log(`changeFollowupDate called with event: ${event}, index: ${index}, ufp: ${JSON.stringify(ufp)}`);
      if(ufp.FollowupDate) {
          this.followupDateChangeInProgress[index] = true;
          Vue.set(this.followupActionPlanLoading, index, true);
          let payload = {
              SiteID: this.siteID,
              EmployeeID: this.userID,
              EmployeeManuallyAssigned: ufp.EmployeeManuallyAssigned, 
              AssignedToEmpID: ufp.AssignedToEmpID,
              TaskTypeID: ufp.TaskTypeID,
              TeamioPatientID: this.patientID,
              FollowupDate: moment(newFollowupDate).format('YYYY-MM-DD'),
              OldFollowupDate: moment(ufp.FollowupDate).format('YYYY-MM-DD'),
              ClaimNum: ufp.ClaimNum,
          }
          console.log('**** payload from Patient Profile changeFollowupDate', payload);
          //console.log(`changeFollowupDate payload: ${JSON.stringify(payload)}`);

          try {
              await this.updateFollowupDate(payload);
              Vue.set(ufp, "FollowupDate", moment(newFollowupDate).format('YYYY-MM-DD'));
              Vue.set(ufp, 'pastDue', 'false');
              if(moment(newFollowupDate).isSame(moment(), 'day')) {
                  Vue.set(ufp, 'dueToday', 'true');
              }
              this.newFollowupDates[index] = null;
          } catch (err) {
              console.error(err);
          } finally {
              Vue.set(this.followupActionPlanLoading, index, null);
              this.followupDateChangeInProgress[index] = false;
              this.$refs[`${index}-change-followup-date-dropdown`][0].hide(true);
          }
      }
    },

    async saveInitialFollowupDate() {
      this.setLoading(true);
      this.initialFollowupInProgress = true;
      if(!this.initialFollowupDate) {        
        this.$toast.error('Please select a date first before saving');
        //this.setLoading(false);
        this.initialFollowupInProgress = false;
        return false;
      }
      
      if(!this.followupType || this.followupType === 0) {        
        this.$toast.error('Please select a followup type of Unscheduled tx or Recall first before saving');
        this.initialFollowupInProgress = false;
        //this.setLoading(false);
        return false;
      }

      let payload = {
          CreatedAt: moment().format('YYYY-MM-DD'),
          SiteID: this.siteID,
          CreatedAutomatically: 1,
          AssignedToEmpID: this.userID,
          DueDate: moment(this.initialFollowupDate).format('YYYY-MM-DD'),
          StatusID: 1,
          TaskTypeID: this.followupType,
          PatNum: this.patNum,
          UserODNum: this.user.ODUserNum,
          DefaultDB: this.user.DefaultDB,
          TeamioPatientID: this.patientID,
          FollowupDate: moment(this.initialFollowupDate).format('YYYY-MM-DD'),
          Active: 1,
          Attempts: 0,
          BelongsToListID: this.belongsToLists ? this.getBelongsToListID(this.followupType) : null,
          InitialFollowupDateSet: 1, // this is how we are tracking that this task is created automatically BUT also is for setting initial follow up
          InitialFollowupCreatedOn: moment().format('YYYY-MM-DD'),
          ...(this.initialFollowupNote && { initialFollowupNote: this.initialFollowupNote })
      }
      console.log(`saveInitialFollowupDate Patient Profile payload: ${JSON.stringify(payload)}`);

      try {
          await this.updateInitialFollowupDate(payload);
          this.$refs.initialFollowupDropdown.hide(true);
          this.$toast.success('Your followup has been created');
          this.setDismissSuccessCountDown(1);
          this.$router.go();
      } catch (err) {
          console.error(err);
          this.initialFollowupInProgress = false;
      } finally {
        this.setLoading(false);
      }
    },

    runSureOk(confirmFor) {
        switch(confirmFor) {
            case 'REMOVE-INITIAL-FOLLOWUP-DATE': 
                                                    //console.log(`now removing initial followup date`); 
                                                    this._removeInitialFollowupDate(); 
                                                    break;
            case 'STOP-FOLLOWUPS':                  //console.log(`now stopping future followups`); 
                                                    this._stopFollowups(); 
                                                    break;
            case 'RESET-FOLLOWUPS':                  //console.log(`now resetting followups`); 
                                                    this._resetFollowups(); 
                                                    break;
            default: break;
        }
    },

    async _removeInitialFollowupDate() {
        let index = this.followupIndex;
        let ufp = this.followupParams;

        this.followupDateChangeInProgress[index] = true;
        Vue.set(this.followupActionPlanLoading, index, true);

        let payload = {
            SiteID: this.siteID,
            TeamioPatientID: this.patientID,
            ClaimNum: ufp.ClaimNum ? ufp.ClaimNum : null,
            TaskTypeID: ufp.TaskTypeID,
            EmployeeID: this.userID,
            OldFollowupDate: moment(ufp.FollowupDate).format('YYYY-MM-DD'),
        }

        //console.log(`payload: ${JSON.stringify(payload)}`);

        try {
            await this.removeInitialFollowupDate(payload); 
            this.upcomingFollowups.splice(index, 1);

        } catch (err) {            
            console.error(err);
        } finally {
            Vue.set(this.followupActionPlanLoading, index, null);
            this.followupDateChangeInProgress[index] = false;
            this.$refs[`${index}-change-followup-date-dropdown`][0].hide(true);
        }

    },

    async _stopFollowups() {
      let index = this.followupIndex;
      let ufp = this.followupParams;

      this.followupDateChangeInProgress[index] = true;
      Vue.set(this.followupActionPlanLoading, index, true);

      let payload = {
          SiteID: this.siteID,
          TeamioPatientID: this.patientID,
          ClaimNum: ufp.ClaimNum ? ufp.ClaimNum : null,
          TaskTypeID: ufp.TaskTypeID,
          EmployeeID: this.userID,
      }

      console.log(`payload: ${JSON.stringify(payload)}`);

      try {
          await this.stopFollowups(payload);
          Vue.set(ufp, "FollowupStopped", 1);
          Vue.set(ufp, "FollowupDate", null);
      } catch (err) {            
          console.error(err);
      } finally {
          Vue.set(this.followupActionPlanLoading, index, null);
          this.followupDateChangeInProgress[index] = false;
          this.$refs[`${index}-change-followup-date-dropdown`][0].hide(true);
      }
    },

    async _resetFollowups() {
      let index = this.followupIndex;
      let ufp = this.followupParams;

      this.followupDateChangeInProgress[index] = true;
      Vue.set(this.followupActionPlanLoading, index, true);

        let payload = {
            SiteID: this.siteID,
            TeamioPatientID: this.patientID,
            ClaimNum: ufp.ClaimNum ? ufp.ClaimNum : null,
            TaskTypeID: ufp.TaskTypeID,
            EmployeeID: this.userID,
        }

        //console.log(`payload: ${JSON.stringify(payload)}`);

        try {
            await this.resetFollowups(payload);
            Vue.set(ufp, "FollowupDate", null);
            Vue.set(ufp, "Status", 'Not Called');
            Vue.set(ufp, "Attempts", 0);
            Vue.set(ufp, "FollowupStopped", 0);
            Vue.set(ufp, "TeamioFollowupStatusID", null);
            
        } catch (err) {            
            console.error(err);
        } finally {
            Vue.set(this.followupActionPlanLoading, index, null);
            this.followupDateChangeInProgress[index] = false;
        }
    },

    async createFollowup(event, ufp, index) {
        //console.log(`in createFollowup, index: ${index}`);
        event.preventDefault();

        let detailPageName = this.getDetailPageName(ufp.TaskTypeID);

        /*
        if(this.lastFollowedUpWithTeamioPatientID !== null) {
            console.log(`Perhaps the user clicked too fast on the same link. Ignoring this click`);
            return false;
        }*/
    
        Vue.set(this.loadingStatus, index, true);
        let claimNum = ufp.ClaimNum;
        this.teamioPatientID = this.patientID;
        this.lastFollowedUpWithTeamioPatientID = this.teamioPatientID;
        //console.log(`In AdminTable createFollowup, patNum: ${patNum}, teamioPatientID: ${this.teamioPatientID} and claimNum: ${claimNum}`);

        let query;

        let attemptTrackingPayload = {
                cacheName: process.env.VUE_APP_MOMENTO_CACHE,
                cacheSet: 'fuattempts',
                element: this.teamioPatientID
        }        
        console.log('attemptTrackingPayload', attemptTrackingPayload);


        try {

            await this.startTrackingFUAttempt(attemptTrackingPayload);
            this.attemptTracking = true;

            switch(detailPageName) {
                case 'OverdueRecallDetails' :       query = {
                                                        type: 'OVERDUE_RECALL_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;

                case 'unscheduledPatientDetails':   query = {
                                                        type: 'UNSCHEDULED_TREATMENT_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'BrokenAppointmentDetails':    query = {
                                                        type: 'BROKEN_APPOINTMENT_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        adjustments: localStorage.getItem('brokenApptAdjustments') || '',
                                                        codes: localStorage.getItem('brokenApptCodes') || '',
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'AgingDetails':                query = {
                                                        type: 'AGING_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        claimID: claimNum,
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'OutstandingPreAuthDetails':   query = {
                                                        type: 'OUTSTANDING_PREAUTH_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        claimID: claimNum,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'ProcessedPreAuthDetails':     query = {
                                                        type: 'PROCESSED_PREAUTH_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'PatientBalancesDetails':      query = {
                                                        type: 'PATIENT_BALANCES_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                default: break;

            }        
            
            let results = await this.runQuery(query);

            const followupHandle = this.$showPanel({
                component : CreateFollowup,
                openOn: 'right',
                width: 1020,
                disableBgClick: true,
                disableEscClick: true,
                cssClass: 'followUpPanelClass',
                props: {
                    teamioPatientID : this.teamioPatientID,
                    claimNum: claimNum,
                    results: results,
                    detailPageName: detailPageName
                }
            });

            var _this = this;

            followupHandle.promise
                .then(async(result) => {
                //console.log(`Create Followup slide-panel handle promise done. Result: ${JSON.stringify(result)}`);
                //let originalDataIndex = this.getOriginalDataIndex(props);
                //console.log(`originalDataIndex: ${originalDataIndex}`);
                
                if(result.Status && result.Attempts) {
                    //console.log('i am in followup handle promise');
                    Vue.set(ufp, "Status", result.Status);
                    Vue.set(ufp,"TeamioFollowupStatusID", result.TeamioFollowupStatusID);
                    Vue.set(ufp, "FollowupDate", result.FollowupDate); 
                    Vue.set(ufp, "Attempts", result.Attempts);
                    Vue.set(ufp, "AttemptDetails", result.AttemptDetails);
                    Vue.set(ufp, "pastDue", 'false');
                    Vue.set(ufp, "completed", 'true');
                    Vue.set(ufp, result.StatusUpdatedAtPropertyName, result.StatusUpdatedAtPropertyNameValue);
                }
                //console.log(`after closing we got:  ${result.nextVisit}`);

                if(_this.attemptTracking) {      
                    console.log('inside attempttracking, teamio patient id', _this.teamioPatientID);
                    Vue.set(_this, 'lastFollowedUpWithTeamioPatientID', null);
                    Vue.set(_this, 'attemptTracking', false);                    
                    await _this.stopTrackingFUAttempt(attemptTrackingPayload);
                    console.log('called stop tracking');                    
                }
                
                Vue.set(ufp,"NextVisit",result.nextVisit);
                Vue.set(ufp, 'teamioActive', false);
                Vue.set(this.data[index], 'teamioActive', false);
                Vue.set(this.loadingStatus, index, false);

                this.setErrored(false);
                this.setMessage('');
            });
        } catch(err) {
          console.log(err);
          if(err.message === "exists") {
                console.log('got exists error');
                this.$toast.error(`There's already someone following-up with this patient`);
                //this.$toast.error(`There's already someone following-up with this patient. If you believe this is wrong then <button type="button" class="btn clear">click here</button> to clear the cache`);
                Vue.set(this.data[index], 'teamioActive', false);
                Vue.set(this.loadingStatus, index, false);
                window.scrollTo(0, 0);
                return false;
            } else {                
                Vue.set(this.loadingStatus, index, false);
            }  
        }
    },

    async loadFamilyMemberInfo(patNum) {
      //console.log(`now loading family member info for PatNum: ${patNum}`);
      let query = {
        type: 'PATIENT_PROFILE',
        siteID: this.siteID,
        patNum: patNum,
        country: localStorage.getItem('country') || 'USA'
      };
      this.familyMembersDetails[patNum] = await this.runQuery(query);
    },

    getBelongsToListID(taskTypeID) {
      console.log(`taskTypeID: ${taskTypeID}`);
      // Numeric compare: the radio's value is the STRING "4"/"3", the list's TaskTypeID a number — with
      // === this never matched and every follow-up started here lost its list (fixed 2026-10-08).
      let lists = this.belongsToLists.filter(a => Number(a.TaskTypeID) === Number(taskTypeID));
      if(lists.length > 0) {
        return lists[0].ListID;
      }
    },

    getAge(birthDate) {
      return moment().diff(birthDate, 'years',false);
    },

    getGender(gender) {
      return gender === 1 ? 'F' : 'M';
    },

    origToggleBlock(memberIndex) {
      this.familyMembers.forEach((each, index)=>{
        if(memberIndex !== index){
          each.isShown = false;
        }else{
          each.isShown = !each.isShown
        }
      })
    },

    async toggleBlock(memberIndex, patNum) {
      let _this = this;
      this.isShown.forEach(async (item, index) => {
        if(memberIndex !== index) {
          Vue.set(_this.isShown, index, false);
        } else {
          Vue.set(_this.isShown, index, !_this.isShown[index]);
          /*
          if(_this.isShown[index] && !this.familyMembersDetails[patNum])
            await this.loadFamilyMemberInfo(patNum);
          */
        }
      });
    },

    successCountDownChanged(count) {
      this.setDismissSuccessCountDown(count);
    },

    getParams(teamioPatientID, claimNum=null) {
        return {
            patientID: teamioPatientID,
            claimID: claimNum
        }
    },

    gotoViewSubmission(formSubmissionID) {
      let routeData = this.$router.resolve(
        {
          path: `/app/forms/view-submission/${formSubmissionID}`
        }
      );
      window.open(routeData.href, '_blank');
    },

    /**
     * "Treatment Plans Signed" — resolve a short-lived presigned URL for the
     * signed-plan PDF and open it in a new tab. Only called for rows that have
     * a pdfId. On failure we surface an alert and no-op (the URL is non-fatal).
     */
    async onOpenSignedPlanPdf(row) {
      if (!row || row.pdfId === null || row.pdfId === undefined) return;
      const planKey = row.treatPlanNum;
      // Resolving the presigned URL is a round-trip (a cold Lambda can take a
      // few seconds). Hold an "Opening…" state so the row doesn't look inert,
      // and ignore repeat clicks while it's in flight.
      if (this.openingPlanPdf[planKey]) return;
      this.$set(this.openingPlanPdf, planKey, true);
      try {
        const url = await this.fetchSignedPdfUrl({ siteID: this.siteID, pdfId: row.pdfId });
        if (url) window.open(url, '_blank');
      } catch (err) {
        console.error('[PatientProfile] onOpenSignedPlanPdf error:', err);
        this.$swal({
          title: 'Could not open PDF',
          text: 'The signed plan PDF could not be opened right now. Please try again in a moment.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#4285f4'
        });
      } finally {
        this.$set(this.openingPlanPdf, planKey, false);
      }
    },

    /**
     * A signed-plan PDF is "broken" when the render is dead rather than in
     * flight: the backend flags the newest row as stale-processing (pdfStuck)
     * or the render outright failed. Written strictly (`=== true` /
     * `=== 'failed'`) so a cached pre-upgrade payload — which has neither field
     * — reads as not-broken and the row degrades to today's plain text.
     */
    isSignedPdfBroken(row) {
      if (!row) return false;
      return row.pdfStuck === true || row.pdfStatus === 'failed';
    },

    /**
     * "Treatment Plans Signed" — re-dispatch a dead PDF render. The backend
     * accepts both failed and stale-processing rows; on accept we hold a
     * per-row "Retrying…" state and re-fetch the section once the worker has
     * had time to finish (renders normally take ~1-2s), so the row flips to a
     * clickable link on its own. 409 = not actually retryable (still rendering
     * or already complete) — surface the server's reason verbatim.
     */
    async onRetrySignedPlanPdf(row) {
      if (!row || row.pdfRetryPdfId === null || row.pdfRetryPdfId === undefined) return;
      const planKey = row.treatPlanNum;
      if (this.retryingPlanPdf[planKey]) return;   // ignore double-taps
      const patNumAtRetry = this.patientNum;
      this.$set(this.retryingPlanPdf, planKey, true);
      try {
        await this.retrySignedPlanPdf({ siteID: this.siteID, pdfId: row.pdfRetryPdfId });
        const timer = setTimeout(async () => {
          // Bail if the profile moved to another patient while we waited —
          // the summary in the store is no longer this row's.
          if (this.patientNum !== patNumAtRetry) return;
          try {
            await this.fetchSignedPlansSummary({ siteID: this.siteID, patNum: patNumAtRetry });
          } catch (err) {
            console.error('[PatientProfile] signed-plan re-fetch after retry failed:', err);
          } finally {
            this.$set(this.retryingPlanPdf, planKey, false);
          }
        }, 4000);
        this.signedPdfRetryTimers.push(timer);
      } catch (err) {
        console.error('[PatientProfile] onRetrySignedPlanPdf error:', err);
        this.$set(this.retryingPlanPdf, planKey, false);
        this.$swal({
          title: 'Could not retry PDF',
          text: (err && err.message)
            ? err.message
            : 'The signed plan PDF could not be regenerated right now. Please try again in a moment.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#4285f4'
        });
      }
    },

    // Feature request event handlers
    onFeatureRequestSent(data) {
      // Show success message
      this.$swal({
        title: 'Request Sent!',
        text: `Your ${data.featureName} feature request has been sent successfully. We'll contact you within 1 business day.`,
        icon: 'success',
        confirmButtonText: 'Great!',
        confirmButtonColor: '#4285f4'
      });
      
      // Close any open popovers
      this.showSendFormHelpPopOver = false;
      this.showSendSmsHelpPopOver = false;
      this.showSendEmailHelpPopOver = false;
    },

    onFeatureRequestError(data) {
      // Show error message
      this.$swal({
        title: 'Request Failed',
        text: `Sorry, we couldn't send your ${data.featureName} feature request. Please try again or contact support directly.`,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
      
      console.error('Feature request error:', data.error);
    },
  },

  computed: {

    /**
     * Gate for the Insurance panel — which carries a Verify button, i.e. a real billed
     * eligibility check.
     *
     * ⛔ Deliberately NOT `canAccessInsuranceVerification`. That gate has a Super-Admin
     * bypass and NO CASL term, and its own comment says it is safe only because it leads to
     * the SOFT-gated /patient-status, which answers 200 {enabled:false} and never alerts.
     * This panel calls /verify, which is flag-GATED and spends money — so it uses the same
     * pair the sidebar nav uses (`topmixin.showInsuranceVerificationNav`): the site flag AND
     * the CASL subject. An Outsourced user, or a role revoked via Feature Permissions, must
     * not get a spend button on a page they can otherwise open.
     *
     * The chip above keeps the looser gate on purpose — it only reads.
     */
    canUseInsurancePanel() {
      if (!this.insuranceVerificationEnabled) return false;
      return !!(typeof this.$can === 'function' && this.$can('view', 'insurance-verification'));
    },

    /**
     * Patient identity for the benefits drawer, in the shape the Insurance Verification
     * dashboard's rows carry (`row.patient`), so the SAME VerificationDrawer component can
     * render from this page without a second variant of it.
     *
     * ⛔ /patient-status deliberately does NOT return a patient block — it answers "what is
     * this patient's insurance", and the caller already knows who they are looking at. So the
     * identity has to come from here, and this is the only place it can.
     *
     * `PatientName` is Open Dental's "Last, First" — split on the FIRST comma only, because a
     * suffix lives on the far side of it ("Mandeville, Craig Jr."), and a name with no comma
     * at all is treated as a last name rather than silently becoming a first name.
     */
    insurancePatient() {
      const d = this.patientData || {};
      const raw = typeof d.PatientName === 'string' ? d.PatientName : '';
      const comma = raw.indexOf(',');
      return {
        patNum: this.patientNum,
        lName: (comma === -1 ? raw : raw.slice(0, comma)).trim(),
        fName: (comma === -1 ? '' : raw.slice(comma + 1)).trim(),
        dob: d.Birthdate || '',
      };
    },

    // Only promise "It will save to commlog..." when the site has opted in
    // (GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG); off by default.
    followupNotePlaceholder() {
      return this.allSettings && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG.PropertyValue === 'TRUE'
        ? 'Optional. Add notes here. It will save to commlog...'
        : 'Optional. Add notes here...';
    },

    patientNum() {
      return this.patientID.substring(this.patientID.lastIndexOf('-')+1, this.patientID.length);
    },

    // Numeric PatNum for PatientTasksCard — Firestore array-contains on
    // taggedPatients is type-strict (8814 !== "8814"), and patientNum is a STRING.
    patientNumInt() {
      return parseInt(this.patientNum, 10);
    },

    // The patient's collectable balance. Source of truth is PatientAmount
    // (the patient-portion column the Aging "Patient Balances" tab + the
    // CreateFollowup payment SMS flow already use — see CreateFollowup.vue
    // patientBalance computed). EstBalance is OD's estimated post-insurance
    // figure and can be 0 even when PatientAmount is large; falling back
    // to it only avoids zeros when PatientAmount isn't on the row.
    resolvedPatientBalance() {
      if (!this.patientData) return 0;
      const a = Number(this.patientData.PatientAmount);
      if (!isNaN(a) && a !== 0) return a;
      const e = Number(this.patientData.EstBalance);
      if (!isNaN(e) && e !== 0) return e;
      return 0;
    },

    // True when the site has any payment provider configured. Used to gate
    // the Collect Payment button — without a provider, neither the SMS link
    // nor the terminal flow can complete. Trust the sites table flag:
    // saveMoolahConfig only sets PaymentProvider='moolah' after the API
    // key is saved + Moolah accepted webhook registration; stripe-oauth.js
    // only sets PaymentProvider='stripe' + StripeAccountId after the OAuth
    // callback verifies. So provider==='moolah' implies creds saved;
    // provider==='stripe' + StripeAccountId implies OAuth completed.
    hasPaymentProvider() {
      const site = this.getSite ? this.getSite(this.siteID) : null;
      if (!site) return false;
      const p = site.PaymentProvider;
      if (p === 'moolah') return true;
      if (p === 'stripe' && site.StripeAccountId) return true;
      return false;
    },

    // PAYMENT_ENABLED is the master toggle on Settings → Payment. It's a
    // separate concern from "provider configured" — operators can have
    // creds saved but the master switch off (e.g., during setup before
    // they're ready to collect, or temporarily disabled after an issue).
    // Read from teamio.settings; both savePaymentConfig and the
    // saveMoolahConfig auto-enable path write '1' on save.
    paymentEnabled() {
      return !!(this.allSettings && this.allSettings.PAYMENT_ENABLED &&
                this.allSettings.PAYMENT_ENABLED.PropertyValue === '1');
    },

    // Composite gate for the Collect Payment button's enabled state.
    // Show the button always (consistent with Form/Text/Email);
    // disable + popover-explain when either piece is missing.
    canCollectPayment() {
      return this.paymentEnabled && this.hasPaymentProvider;
    },

    // Reviews A7 — prerequisite for the Review Request button: the patient must
    // have at least one channel on file (mobile OR email). Without either there's
    // nothing to send on, so the button disables + popover-explains. Per-site
    // access is gated separately by $can('view','reviews') on the button itself.
    // Reviews upsell: only when the site hasn't PAID (REVIEWS_PAID_FOR off). $can('view','reviews')
    // is also false for a paid site's Outsourced/overridden roles — those must NOT see an upsell.
    showReviewsUpsell() {
      if (!this.allSettings) return false;
      if (typeof this.$can === 'function' && this.$can('view', 'reviews')) return false;
      const s = this.allSettings.REVIEWS_PAID_FOR;
      return !(s && s.PropertyValue === 'TRUE');
    },

    reviewRequestPrereqMet() {
      const p = this.patientData;
      if (!p) return false;
      const hasPhone = !!(p.WirelessPhone && String(p.WirelessPhone).trim() !== '');
      const hasEmail = !!(p.Email && String(p.Email).trim() !== '');
      return hasPhone || hasEmail;
    },

    // Specific reasons surfaced in the disabled-state popover. Order
    // matters — operator should see the FIRST thing they need to fix at
    // the top. PAYMENT_ENABLED first because that's the master toggle;
    // provider-config second because it only matters once enabled.
    collectPaymentDisabledReasons() {
      const reasons = [];
      if (!this.paymentEnabled) {
        reasons.push({
          icon: 'fa-power-off',
          text: 'Payment collection is turned off for this site.'
        });
      }
      if (this.paymentEnabled && !this.hasPaymentProvider) {
        const site = this.getSite ? this.getSite(this.siteID) : null;
        if (site && site.PaymentProvider === 'stripe' && !site.StripeAccountId) {
          reasons.push({
            icon: 'fa-link',
            text: 'Stripe Connect setup is incomplete. Reconnect Stripe.'
          });
        } else {
          reasons.push({
            icon: 'fa-cog',
            text: 'No payment provider is configured. Pick Moolah or Stripe.'
          });
        }
      }
      return reasons;
    },

    // Slice 11 Phase C — Tx Plans dispatcher state surfaced from the
    // txPresenter Vuex module. Same module backs the Builder + Send modal,
    // so opening these from Patient Profile feeds the same state machine.
    txPlansDispatcherOpen() {
      return this.$store.state.txPresenter.patientTxPlansDispatcher.open;
    },
    txPlansDispatcherLoading() {
      return this.$store.state.txPresenter.patientTxPlansDispatcher.loading;
    },
    sendPlanOpen() {
      return this.$store.state.txPresenter.sendPlan.open;
    },
    /**
     * SendPlanModal expects `:patient` with fName/lName/wirelessPhone/email so
     * it can pre-fill SMS / email recipients. From Patient Profile, the data
     * is already on the dispatcher (loaded by getTxPresentationDetail);
     * forward it through unchanged.
     */
    sendPlanPatientForModal() {
      return this.$store.state.txPresenter.patientTxPlansDispatcher.patient;
    },

    // "Treatment Plans Signed" section — the patient's Tx Presenter–signed
    // plans + per-plan consent-form status (from /get-tx-signed-plans-summary).
    signedPlans() {
      return this.$store.state.txPresenter.signedPlansSummary.plans;
    },

    dataToDisplay() {
      if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
    },

    familyMembers() {
      /* only show family members excluding the one currently being displayed */
      if(this.dataToDisplay && this.dataToDisplay[0]) {
        return this.dataToDisplay[0].filter(f => f.PatNum !== parseInt(this.patientNum,10))
        //.map(obj => ({...obj, isShown: false}))
      }
    },

    patientData() {
      if(this.dataToDisplay)
        return this.dataToDisplay[1][0];
    },

    scheduledAppointments() {
      if(this.dataToDisplay)
        return this.dataToDisplay[2];
    },

    // Next appointment date for quick stats
    nextAppointmentDate() {
      if (this.scheduledAppointments && this.scheduledAppointments.length > 0) {
        const nextAppt = this.scheduledAppointments[0];
        return nextAppt.AptDateTime ? this.formatDateShort(nextAppt.AptDateTime) : null;
      }
      return null;
    },

    // Check if recall is overdue
    isRecallOverdue() {
      if (this.patientData && this.patientData.DateDue) {
        const dueDate = new Date(this.patientData.DateDue);
        return dueDate < new Date();
      }
      return false;
    },

    unScheduledTreatments() {
      if(this.dataToDisplay)
        return this.dataToDisplay[3];
    },

    brokenAppointments() {
      if(this.dataToDisplay)
        return this.dataToDisplay[4];
    },

    plannedAppointments() {
      if(this.dataToDisplay)
        return this.dataToDisplay[5];
    },

    upcomingFollowups() {
      if(this.dataToDisplay && this.dataToDisplay[6].length > 0)
        return this.dataToDisplay[6]
    },

    /*
    Follow-ups redesign (Phase 5) — which Follow-ups section renders. Not a Super Admin →
    RELEASED.patientProfile (false today), so today's section, always. A Super Admin → the new
    section unless "Classic view" was picked (the same switch as the follow-up pages).
    */
    fuProfileNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('patientProfile', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    // What this page already loaded about the patient, for the new section's Details column.
    fuProfileFacts() {
      return profileFactsFrom({
        patient: this.patientData,
        unscheduledTreatments: this.unScheduledTreatments,
        brokenAppointments: this.brokenAppointments,
      });
    },

    /*
    this is specifically to filter out the follow-ups where follow-up date is NULL (for "don't follow up" statuses)
    These will be displayed on the profile page under "Follo-ups for the patient"
    */
    upcomingFollowupsToDisplay(){
       if(this.dataToDisplay && this.dataToDisplay[6].length > 0)
        return this.dataToDisplay[6].filter(t => t.FollowupDate !== null)
    },

    commlog() {
      if(this.dataToDisplay && this.dataToDisplay[9].length > 0) {
        if(this.commLogSelectedMode === null) {
          return this.dataToDisplay[9];
        } else if(this.commLogSelectedMode === 100) {
          return this.dataToDisplay[9].filter(a => /^(\[Teamio\].*)$/.test(a.Note) === true);
        }
        else {
          return this.dataToDisplay[9].filter(a => a.Mode_ === this.commLogSelectedMode);
        }
      }
    },

    formSubmissions() {
      if(this.dataToDisplay && this.dataToDisplay[10].length > 0) {
        // Sort by DateUpdated in descending order (most recent first)
        return [...this.dataToDisplay[10]].sort((a, b) => {
          const dateA = new Date(a.DateUpdated);
          const dateB = new Date(b.DateUpdated);
          return dateB - dateA; // Descending order
        });
      }
    },

    asapApptList() {
      if(this.dataToDisplay && this.dataToDisplay[11].length > 0) {
        return this.dataToDisplay[11];
      }
    },

    familyMemberPatientData() {
      return function(patNum) {
        if(this.familyMembersDetails.patNum) {
          return this.familyMembersDetails.patNum.data[1][0];
        }
      }
    },

    familyMemberRecallAppointments(patNum) {
      if(this.familyMembersDetails.patNum) {
        return this.familyMembersDetails.patNum.data[2];
      }
    },

    familyMemberScheduledAppointments(patNum) {
      if(this.familyMembersDetails.patNum) {
        return this.familyMembersDetails.patNum.data[3];
      }
    },

    familyMemberUnscheduledTreatments(patNum) {
      if(this.familyMembersDetails.patNum) {
        return this.familyMembersDetails.patNum.data[4];
      }
    },

    familyMemberBrokenAppointments(patNum) {
      if(this.familyMembersDetails.patNum) {
        return this.familyMembersDetails.patNum.data[5];
      }
    },

    unscheduledTxActiveFollowUps() {
      if (this.upcomingFollowups)
        return this.upcomingFollowups.filter(t => t.TaskTypeID === 4)
       
    },

    overdueRecallActiveFollowUps() {
      if (this.upcomingFollowups)
        return this.upcomingFollowups.filter(t => t.TaskTypeID === 3);
    },

    nonRecallAppointments() {
      if(this.dataToDisplay && this.dataToDisplay[7].length > 0)
        return this.dataToDisplay[7];
    },

    belongsToLists() {
      if(this.dataToDisplay && this.dataToDisplay[8].length > 0)
        return this.dataToDisplay[8];
    },

    /*
    in the below function, we were also checking to see if they have non-recall appointments and if they did
    we were not letting them create the follow-up. But we're removing this check just like we did on the main
    Unscheduled Tx page. 
   
         || (this.nonRecallAppointments && this.nonRecallAppointments.length > 0)

    */
    unscheduledTxFollowUpCreationAllowed() {
      if ( 
          
          (this.unscheduledTxActiveFollowUps && this.unscheduledTxActiveFollowUps.length > 0) 
          || 
          (this.unScheduledTreatments && this.unScheduledTreatments.length === 0)
      )
        return false
      else
        return true;
    },

    overdueRecallFollowUpCreationAllowed() {
      if ( 
          (this.overdueRecallActiveFollowUps && this.overdueRecallActiveFollowUps.length > 0) 
          || (this.patientData && this.patientData.RecallScheduled !== null ) 
      )
        return false
      else
        return true;
    },

    saveDisabled() {
      return this.initialFollowupInProgress || (this.unscheduledTxFollowUpCreationAllowed === false && this.overdueRecallFollowUpCreationAllowed === false);
    },

    formsPaidFor() {      
      if(this.allSettings && this.allSettings.FORMS_PAID_FOR && this.allSettings.FORMS_PAID_FOR.PropertyValue === 'TRUE')
        return true;
      else
        return false;      
    },

    canSendForms() {
      // Forms can only be sent if:
      // 1. Forms feature is enabled (paid for)
      // 2. At least ONE delivery method is available (SMS OR Email)
      if (!this.formsPaidFor) {
        return false;
      }
      
      // Check if at least one delivery method is available
      // We need either SMS feature enabled OR Email feature enabled
      // (patient-specific checks will happen in the sidebar)
      const hasAnyDeliveryMethod = this.smsSendable || this.emailFeatureEnabled;
      
      return hasAnyDeliveryMethod;
    },

    // Site timezone (GENERAL_TIMEZONE) — passed to the shared text-consent rule so the
    // "opted out on M/D/YYYY" date renders in the practice's zone, not UTC.
    smsOptOutTimezone() {
      return (this.allSettings && this.allSettings.GENERAL_TIMEZONE && this.allSettings.GENERAL_TIMEZONE.PropertyValue) || '';
    },

    canSendSMS() {
      let canSend = false;

      if(this.smsSendable) {
        // Shared opt-out rule (texted STOP / OD Text-OK = No / server block) — textConsent.js.
        if(isTextBlocked({ patient: this.patientData, timezone: this.smsOptOutTimezone })) {
          canSend = false;
        } else {
          // The number must be one the send endpoint will actually accept — shared rule,
          // identical to main-service getPatientPhoneByPatNum. GENERAL_SMS_FLAG_CHECK does
          // NOT bypass this: that flag governs whether we consult the patient's Text OK
          // preference (teamio-utility canSendSMSToThisPatient), and no site setting can
          // make a malformed or absent number deliverable. Skipping the check here only
          // enabled a button that 400s on click — which is exactly how a patient with no
          // wireless phone at all reached the endpoint on 2026-09-08.
          canSend = isTextablePhone(this.patientData && this.patientData.WirelessPhone);
        }
      }
      return canSend;
    },

    canSendEmail() {
      // Account-level email capability (shared five-shape parser) AND the patient has an
      // email on file. Identical to the old naive check for bare-string prod values.
      if (!isEmailEnabled(this.allSettings)) return false;
      return !!(this.patientData && this.patientData.Email !== '');
    },

    canAICall() {
      if (!this.isSuperAdmin) return false;
      return this.patientData && this.patientData.WirelessPhone && this.patientData.WirelessPhone.trim() !== '';
    },

    // Feature-level checks (separate from patient data) for the SMS / Email upsell popovers
    // The office HAS texting (else the upsell card) — siteCapability.hasSmsNumber.
    smsFeatureEnabled() {
      return hasSmsNumber(this.allSettings);
    },

    // The office may SEND an SMS. Differs from smsFeatureEnabled only at a WhatsApp-only office
    // (SMS_OUTBOUND_DISABLED): it has a number, so no upsell, but nothing may go out by SMS.
    smsSendable() {
      return isSmsEnabled(this.allSettings);
    },

    emailFeatureEnabled() {
      // Derived email capability via the shared five-shape parser — identical to the
      // old naive `.trim() !== ''` for bare-string prod values, but correctly reports
      // disabled for senderless shapes like "[]"/"{}". See src/utils/siteCapability.js.
      return isEmailEnabled(this.allSettings);
    },

    // Dynamic help messages that show specific reasons for disabled features
    smsDisabledReasons() {
      const reasons = [];
      
      // Check if SMS feature is not enabled at account level
      if (!this.smsFeatureEnabled) {
        reasons.push({
          icon: 'fa-cog',
          text: 'SMS feature is not enabled on your account yet',
          isFeatureIssue: true
        });
      } else if (!this.smsSendable) {
        reasons.push({ icon: 'fa-ban', text: SMS_DISABLED_MESSAGE, isFeatureIssue: true });
      } else {
        // Opt-out first (texted STOP, OD Text-OK = No, or a server/HQ block) — the shared
        // rule renders the SAME sentence the operator would see on a server refusal, so the
        // disabled-button tooltip, the send panel, and the backend all agree.
        const block = getTextBlockReason({ patient: this.patientData, timezone: this.smsOptOutTimezone });
        if (block.blocked) {
          reasons.push({
            icon: 'fa-user-times',
            text: block.message,
            isFeatureIssue: false
          });
        } else {
          // Same shared rule as canSendSMS, and the same reason the operator sees inside
          // the send panel — so the tooltip on the disabled button and the panel agree.
          const phoneProblem = describePhoneProblem(this.patientData && this.patientData.WirelessPhone);
          if (phoneProblem) {
            reasons.push({
              icon: 'fa-phone',
              text: phoneProblem,
              isFeatureIssue: false
            });
          }
        }
      }

      return reasons;
    },

    // The tip under the SMS popover follows the block type. A texted opt-out can only be lifted by
    // the PATIENT texting START (the reason already says so), so no Open Dental tip is shown; an
    // Open Dental flag points staff at the text setting there; anything else is a phone problem.
    smsDisabledTip() {
      if (this.smsFeatureEnabled && !this.smsSendable) return null; // nothing in the patient's record would change it
      const block = getTextBlockReason({ patient: this.patientData, timezone: this.smsOptOutTimezone });
      if (block.blocked) {
        return block.code === TEXT_BLOCK_CODES.OPTED_OUT_BY_TEXT
          ? null
          : "Change the patient's text message setting in Open Dental to enable SMS.";
      }
      return 'Update patient contact info in Open Dental to enable SMS.';
    },

    emailDisabledReasons() {
      const reasons = [];
      
      // Check if Email feature is not enabled at account level
      if (!this.emailFeatureEnabled) {
        reasons.push({
          icon: 'fa-cog',
          text: 'Email feature is not configured on your account yet',
          isFeatureIssue: true
        });
      } else {
        // Feature is enabled, check patient-specific issues
        if (!this.patientData || this.patientData.Email === '') {
          reasons.push({
            icon: 'fa-user',
            text: 'Patient does not have an email address in Open Dental',
            isFeatureIssue: false
          });
        }
      }
      
      return reasons;
    },

    formsDisabledReasons() {
      const reasons = [];
      
      if (!this.formsPaidFor) {
        reasons.push({
          icon: 'fa-credit-card',
          text: 'Forms feature has not been purchased for your account',
          isFeatureIssue: true
        });
      }
      
      // Check if no delivery methods are available
      if (this.formsPaidFor && !this.smsSendable && !this.emailFeatureEnabled) {
        reasons.push({
          icon: 'fa-ban',
          text: 'Neither SMS nor Email is configured for your account. At least one delivery method is required to send forms.',
          isFeatureIssue: true
        });
      } else if (this.formsPaidFor) {
        // If forms are enabled but only one delivery method is missing, provide helpful info
        if (!this.smsSendable && this.emailFeatureEnabled) {
          reasons.push({
            icon: 'fa-info-circle',
            text: this.smsFeatureEnabled ? 'Texting is turned off for this office. Forms can only be sent via Email.' : 'SMS is not configured. Forms can only be sent via Email.',
            isFeatureIssue: false
          });
        } else if (this.smsSendable && !this.emailFeatureEnabled) {
          reasons.push({
            icon: 'fa-info-circle',
            text: 'Email is not configured. Forms can only be sent via SMS.',
            isFeatureIssue: false
          });
        }
      }
      
      return reasons;
    },

},

watch: {
  allSettings(newValue, oldValue) {
    //console.log(`watch changed. Old value: ${JSON.stringify(oldValue)}, \n New value: ${JSON.stringify(newValue)}`);
    if(!oldValue) {
      this.init();
    }
  },
  patientData(val) {
    if (val) this.maybeOpenCollectPaymentFromQuery();
  },
  // The pre-fill belongs to the Action Center open only — a later manual "Collect Payment" starts clean.
  showCollectPaymentModal(open) {
    if (!open) this.collectPaymentPrefill = null;
  }
}



  
};
</script>

<style src="./PatientProfile.scss" lang="scss" scoped />
<!-- Follow-ups redesign (Phase 5): the new Follow-ups section's placement only. -->
<style src="../Followups/patient-profile-followups.scss" lang="scss" scoped />
<style>
  .cursor-pointer {
    cursor: pointer;
  }
  .VueTables{
    overflow: auto;
  }
  .VueTables th{
    font-weight: bold;
    background-color: transparent !important;
  }
  .VuePagination{
    flex-direction: row-reverse;
    float: right;
    margin: 0 !important;
    padding: 0 !important;
  }

  .patient-send-text {
    width: 350;
  }

  /* Fix popover width and text layout issues - More aggressive approach */
  .popover.feature-help-popover {
    max-width: 450px !important;
    min-width: 350px !important;
    width: 400px !important;
  }

  .popover.feature-help-popover .popover-body {
    white-space: normal !important;
    word-wrap: break-word !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
    width: 100% !important;
    min-width: 300px !important;
    padding: 15px !important;
  }

  .popover.feature-help-popover .help_popover {
    white-space: normal !important;
    word-wrap: break-word !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
    width: 100% !important;
    min-width: 300px !important;
  }

  .popover.feature-help-popover .help_popover .d-flex {
    flex-wrap: nowrap !important;
    align-items: flex-start !important;
    display: flex !important;
    width: 100% !important;
  }

  .popover.feature-help-popover .help_popover .d-flex p {
    flex: 1 !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    line-height: 1.4 !important;
    margin-left: 8px !important;
    width: auto !important;
    min-width: 200px !important;
  }

  .popover.feature-help-popover .bg-light {
    white-space: normal !important;
    word-wrap: break-word !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    width: 100% !important;
  }

  .popover.feature-help-popover .bg-light small {
    white-space: normal !important;
    word-wrap: break-word !important;
    word-break: normal !important;
    overflow-wrap: break-word !important;
    display: block !important;
    line-height: 1.3 !important;
    width: 100% !important;
  }

     /* Additional Bootstrap popover overrides - Global approach */
   .popover {
     max-width: 450px !important;
     min-width: 350px !important;
   }

   .popover .popover-body {
     min-width: 300px !important;
     white-space: normal !important;
     word-wrap: break-word !important;
     word-break: normal !important;
     overflow-wrap: break-word !important;
   }

   .popover.bs-popover-right .popover-body,
   .popover.bs-popover-left .popover-body {
     min-width: 300px !important;
     white-space: normal !important;
   }

   .popover .help_popover * {
     white-space: normal !important;
     word-wrap: break-word !important;
     word-break: normal !important;
     overflow-wrap: break-word !important;
   }

   /* Force all text in popovers to display normally */
   .popover * {
     white-space: normal !important;
     word-spacing: normal !important;
     letter-spacing: normal !important;
     text-orientation: mixed !important;
     writing-mode: horizontal-tb !important;
   }

   /* Feature Contact Banner - Prominent but clean */
   .feature-contact-banner {
     display: flex;
     align-items: center;
     gap: 12px;
     padding: 14px 16px;
     background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
     border-radius: 10px;
     border: 1px solid #93c5fd;
   }

   .feature-contact-icon {
     width: 36px;
     height: 36px;
     background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
     border-radius: 8px;
     display: flex;
     align-items: center;
     justify-content: center;
     flex-shrink: 0;
     box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
   }

   .feature-contact-icon i {
     color: white;
     font-size: 16px;
   }

   .feature-contact-content {
     display: flex;
     flex-direction: column;
     gap: 4px;
   }

   .feature-contact-content strong {
     color: #1e40af;
     font-size: 13px;
     font-weight: 700;
   }

   .feature-contact-link {
     display: inline-flex;
     align-items: center;
     gap: 6px;
     padding: 6px 12px;
     background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
     color: white !important;
     font-weight: 600;
     font-size: 12px;
     border-radius: 6px;
     text-decoration: none !important;
     box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
     transition: all 0.2s ease;
   }

   .feature-contact-link:hover {
     background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
     transform: translateY(-1px);
     box-shadow: 0 3px 10px rgba(59, 130, 246, 0.4);
     color: white !important;
     text-decoration: none !important;
   }

   .feature-contact-link i {
     font-size: 11px;
   }

   .ai-call-btn {
     padding: 0 4px;
     font-size: 14px;
     line-height: 1;
     vertical-align: middle;
     background: transparent;
     border: 1px solid transparent;
     border-radius: 3px;
     cursor: pointer;
     opacity: 0.7;
     transition: opacity 0.15s;
   }
   .ai-call-btn:hover:not(:disabled) {
     opacity: 1;
     border-color: #ccc;
   }
   .ai-call-btn:disabled {
     opacity: 0.3;
     cursor: not-allowed;
   }
   .ai-call-btn.ai-call-loading {
     opacity: 0.5;
   }

   .ai-turn {
     padding: 8px 12px;
     border-radius: 8px;
     animation: fadeInUp 0.3s ease-out;
   }
   .ai-turn-agent {
     background: #f0f8ff;
     border-left: 3px solid #4a90e2;
   }
   .ai-turn-patient {
     background: #f8f9fa;
     border-left: 3px solid #888;
   }
   @keyframes fadeInUp {
     from { opacity: 0; transform: translateY(6px); }
     to   { opacity: 1; transform: translateY(0); }
   }

   /* ==============================================================
      PATIENT PROFILE — Modernized action button row (Option 1)
      Hierarchy: primary CTA + grouped secondary "Send" cluster +
      tertiary tool button + small AI-call chip pushed right.
      ============================================================== */
   .profile-actions-modern {
     display: flex;
     align-items: center;
     flex-wrap: wrap;
     gap: 10px;
     padding: 4px 0 8px;
   }

   /* Primary CTA — Initiate a Follow-up dropdown stays prominent */
   .profile-actions-modern .profile-action-primary > .btn,
   .profile-actions-modern .profile-action-primary .dropdown-toggle {
     padding: 6px 14px;
     font-weight: 600;
     border-radius: 8px;
     box-shadow: 0 1px 2px rgba(40, 167, 69, 0.18);
   }

   /* Grouped secondary "Send to patient" cluster */
   .profile-actions-modern .profile-send-group {
     display: inline-flex;
     align-items: center;
     gap: 6px;
     padding: 4px 10px 4px 12px;
     background: #f6f8fa;
     border: 1px solid #e3e7ec;
     border-radius: 10px;
   }

   .profile-actions-modern .profile-send-group-label {
     font-size: 10.5px;
     font-weight: 600;
     text-transform: uppercase;
     letter-spacing: 0.6px;
     color: #6b7280;
     margin-right: 4px;
     user-select: none;
   }

   .profile-actions-modern .profile-send-btn {
     padding: 4px 12px;
     font-size: 13px;
     font-weight: 500;
     border-radius: 6px;
     background: #fff;
     border-color: #d1d5db;
     color: #374151;
     transition: all 0.15s ease;
   }

   .profile-actions-modern .profile-send-btn:hover:not(:disabled) {
     background: #f3f4f6;
     border-color: #9ca3af;
     color: #111827;
   }

   .profile-actions-modern .profile-send-btn:disabled {
     opacity: 0.55;
   }

   .profile-actions-modern .profile-send-btn i.fa {
     color: #6b7280;
   }

   /* Tertiary tool — Get Form QR. Distinct from the Send group. */
   .profile-actions-modern .profile-tool-btn {
     padding: 6px 14px;
     font-size: 13px;
     font-weight: 500;
     border-radius: 8px;
     border-width: 1px;
   }

   /* Small icon-only AI Call chip, pushed to the far right */
   .profile-actions-modern .profile-ai-call-btn {
     margin-left: auto;
     width: 34px;
     height: 34px;
     padding: 0;
     border-radius: 50%;
     display: inline-flex;
     align-items: center;
     justify-content: center;
     font-size: 15px;
   }

   /* Tighten spacing of the disabled-button-wrapper inside the new layout
      (the original had ml-2/mb-2 utility margins; we drop them for cleaner gaps). */
   .profile-actions-modern .disabled-button-wrapper {
     display: inline-flex;
     margin: 0;
   }

   /* ==============================================================
      Phone responsive layout — Lakeshore Dental iPhone screenshot,
      2026-05-04. The SEND TO PATIENT cluster (Form / Text / Email)
      was overflowing horizontally past the right edge because
      .profile-send-group uses display: inline-flex with no wrap, and
      the group itself was sitting on the same row as Initiate Follow-up.
      Below 768px we let the group wrap, take its own full-width row,
      and put its label on its own line so the buttons can expand
      naturally with thumb-sized touch targets.
      ============================================================== */
   @media (max-width: 767.98px) {
     .profile-actions-modern {
       gap: 8px;
     }
     .profile-actions-modern .profile-action-primary {
       width: 100%;
     }
     .profile-actions-modern .profile-action-primary > .btn,
     .profile-actions-modern .profile-action-primary .dropdown-toggle {
       width: 100%;
     }
     .profile-actions-modern .profile-send-group {
       flex-wrap: wrap;
       width: 100%;
       justify-content: flex-start;
     }
     .profile-actions-modern .profile-send-group-label {
       width: 100%;
       margin-right: 0;
       margin-bottom: 4px;
     }
     .profile-actions-modern .profile-send-btn {
       flex: 1 1 auto;
       min-width: 0;
     }
     .profile-actions-modern .profile-tool-btn {
       flex: 1 1 auto;
     }
     /* Drop the margin-left:auto so the AI call chip doesn't strand on
        the right when the layout wraps. */
     .profile-actions-modern .profile-ai-call-btn {
       margin-left: 0;
     }
   }

</style>
